import React, { useEffect, useState } from 'react';
import { ChevronRightIcon, SaveIcon, XIcon, AlertCircleIcon, CheckCircleIcon, Loader2Icon, PlusIcon, TrashIcon, PencilIcon, CircleIcon } from 'lucide-react';
type TabId = 'core' | 'agency' | 'workflow' | 'documents' | 'fees' | 'delivery' | 'communications' | 'review';
interface Tab {
  id: TabId;
  name: string;
  description: string;
  canAccess: boolean;
}
interface Agency {
  agency_id: string;
  agency_code: string;
  agency_name: string;
  jurisdiction_level: string;
}
interface JurisdictionLevel {
  level_id: string;
  level_name: string;
}
interface JurisdictionOverride {
  id: string;
  level: string;
  jurisdiction: string;
  isAvailable: boolean;
  cascades: boolean;
  notes: string;
}
interface WorkflowDefinition {
  workflow_def_id: string;
  workflow_code: string;
  workflow_name: string;
  description: string;
  category: string;
}
interface WorkflowStep {
  step_id: string;
  sequence: number;
  step_code: string;
  step_name: string;
  default_role: string;
  default_sla_days: number;
  overridden_role?: string;
  overridden_sla_days?: number;
}
interface ConditionalWorkflowRule {
  rule_id: string;
  priority: number;
  condition: string;
  alternate_workflow_id: string;
  description: string;
}
interface DocumentType {
  id: string;
  name: string;
  description: string;
  category: string;
}
interface DocumentRequirement {
  id: string;
  documentTypeId: string;
  documentTypeName: string;
  isMandatory: boolean;
  conditionLogic: string;
  targetJurisdiction: string;
  targetApplicantTypes: string[];
  helpText: string;
  isActive: boolean;
}
interface Tariff {
  id: string;
  description: string;
  revenueType: string;
  feeType: string;
  amount: number;
  currency: string;
  unit?: string;
  applicableJurisdiction?: string;
  applicantTypes: string[];
  conditions?: string;
  effectiveFrom: string;
  effectiveUntil?: string;
  isActive: boolean;
}
interface OutputDocument {
  id: string;
  description: string;
  documentTypeId: string;
  documentTypeName: string;
  templateId?: string;
  templateName?: string;
  validityPeriodValue?: number;
  validityPeriodUnit?: 'days' | 'months' | 'years';
}
interface DeliveryConfig {
  primaryOutputDocumentTypeId: string;
  primaryOutputTemplateId?: string;
  outputValidityPeriodValue?: number;
  outputValidityPeriodUnit?: 'days' | 'months' | 'years';
  additionalOutputs: OutputDocument[];
  allowPortalDownload: boolean;
  allowOfficeCollection: boolean;
  defaultCollectionOffices: string[];
  collectionInstructions?: string;
  allowMailDelivery: boolean;
  mailDeliveryFeeId?: string;
  requiresAddressVerification: boolean;
  otherDeliveryInstructions?: string;
  slaTargetDays: number;
  slaBasisEvent: 'submission' | 'payment' | 'last_info';
}
interface PaymentConfig {
  paymentRequired: 'always' | 'never' | 'conditional';
  paymentConditionLogic?: string;
  paymentDueTrigger: string;
  customPaymentDueTrigger?: string;
  allowedPaymentMethods: string[];
  paymentInstructions?: string;
  internalNotes?: string;
}
interface NotificationRule {
  id: string;
  triggerEvent: string;
  channels: Array<'email' | 'sms' | 'portal'>;
  recipientGroups: string[];
  templateId?: string;
  templateName?: string;
  delayValue?: number;
  delayUnit?: 'minutes' | 'hours' | 'days';
  delayType?: 'before' | 'after';
  isEnabled: boolean;
}
interface ManualTemplate {
  id: string;
  templateId: string;
  templateName: string;
  channel: 'email' | 'sms' | 'portal';
  purpose: string;
}
export const CreateServicePage = () => {
  const [currentTab, setCurrentTab] = useState<TabId>('core');
  const [isDraft, setIsDraft] = useState(false);
  const [formData, setFormData] = useState({
    serviceId: 'System Generated upon Save',
    serviceCode: '',
    serviceName: '',
    categoryId: '',
    description: '',
    targetAudiences: [] as string[],
    searchTags: [] as string[],
    metadataSchema: '',
    isActive: true,
    internalNotes: ''
  });
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [isCodeUnique, setIsCodeUnique] = useState<boolean | null>(null);
  const [isCheckingCode, setIsCheckingCode] = useState(false);
  const [agencyFormData, setAgencyFormData] = useState({
    responsibleAgencyId: '',
    supportingAgencies: [] as string[],
    jurisdictionLevelId: '',
    overrides: [] as JurisdictionOverride[]
  });
  const [showOverrideModal, setShowOverrideModal] = useState(false);
  const [currentOverride, setCurrentOverride] = useState<JurisdictionOverride | null>(null);
  const [selectedWorkflowId, setSelectedWorkflowId] = useState<string>('');
  const [workflowSteps, setWorkflowSteps] = useState<WorkflowStep[]>([]);
  const [showStepModal, setShowStepModal] = useState(false);
  const [selectedStep, setSelectedStep] = useState<WorkflowStep | null>(null);
  const [conditionalRules, setConditionalRules] = useState<ConditionalWorkflowRule[]>([]);
  const [documentRequirements, setDocumentRequirements] = useState<DocumentRequirement[]>([]);
  const [showDocumentModal, setShowDocumentModal] = useState(false);
  const [selectedDocument, setSelectedDocument] = useState<DocumentRequirement | null>(null);
  const [documentFilter, setDocumentFilter] = useState({
    mandatory: 'all',
    type: 'all'
  });
  const [tariffs, setTariffs] = useState<Tariff[]>([]);
  const [showTariffModal, setShowTariffModal] = useState(false);
  const [selectedTariff, setSelectedTariff] = useState<Tariff | null>(null);
  const [deliveryConfig, setDeliveryConfig] = useState<DeliveryConfig>({
    primaryOutputDocumentTypeId: '',
    additionalOutputs: [],
    allowPortalDownload: true,
    allowOfficeCollection: false,
    defaultCollectionOffices: [],
    allowMailDelivery: false,
    requiresAddressVerification: true,
    slaTargetDays: 5,
    slaBasisEvent: 'submission'
  });
  const [showOutputModal, setShowOutputModal] = useState(false);
  const [selectedOutput, setSelectedOutput] = useState<OutputDocument | null>(null);
  const [paymentConfig, setPaymentConfig] = useState<PaymentConfig>({
    paymentRequired: 'always',
    paymentDueTrigger: 'submission',
    allowedPaymentMethods: []
  });
  const [notificationRules, setNotificationRules] = useState<NotificationRule[]>([{
    id: 'NTF001',
    triggerEvent: 'Application Submitted',
    channels: ['email', 'sms'],
    recipientGroups: ['Applicant'],
    templateId: 'TPL001',
    templateName: 'Application Confirmation - Email',
    isEnabled: true
  }]);
  const [manualTemplates, setManualTemplates] = useState<ManualTemplate[]>([]);
  const [showNotificationModal, setShowNotificationModal] = useState(false);
  const [selectedNotification, setSelectedNotification] = useState<NotificationRule | null>(null);
  const [showTemplateModal, setShowTemplateModal] = useState(false);
  const [selectedManualTemplate, setSelectedManualTemplate] = useState<ManualTemplate | null>(null);
  const [expandedSection, setExpandedSection] = useState<string | null>(null);
  const [activationAction, setActivationAction] = useState<'draft' | 'activate' | 'keep-active' | 'deactivate'>('draft');
  const [adminNotes, setAdminNotes] = useState('');
  // Add validation checks
  const validationChecks = [{
    id: 'core',
    label: 'Core Information Complete',
    passed: Boolean(formData.serviceCode && formData.serviceName && formData.categoryId)
  }, {
    id: 'agency',
    label: 'Responsible Agency Assigned',
    passed: Boolean(agencyFormData.responsibleAgencyId)
  }, {
    id: 'workflow',
    label: 'Primary Workflow Assigned & Active',
    passed: Boolean(selectedWorkflowId)
  }, {
    id: 'tariffs',
    label: 'Tariffs Defined (if payment required)',
    passed: paymentConfig.paymentRequired === 'never' || tariffs.length > 0
  }, {
    id: 'output',
    label: 'Primary Output Document Type & Template Defined',
    passed: Boolean(deliveryConfig.primaryOutputDocumentTypeId && deliveryConfig.primaryOutputTemplateId)
  }, {
    id: 'sla',
    label: 'Overall SLA Defined',
    passed: deliveryConfig.slaTargetDays > 0
  }];
  const isValidForActivation = validationChecks.every(check => check.passed);
  // Simulated categories data - in real app, fetch from API
  const categories = [{
    category_id: '1',
    category_name: 'Land & Property'
  }, {
    category_id: '2',
    category_name: 'Business Licensing'
  }, {
    category_id: '3',
    category_name: 'Public Safety'
  }];
  const audienceTypes = ['Individual Citizen', 'Registered Business/Company', 'Non-Governmental Organization (NGO)', 'Government Entity', 'Foreign National', 'Specific Business Sector'];
  const agencies: Agency[] = [{
    agency_id: '1',
    agency_code: 'LAND',
    agency_name: 'Land Administration',
    jurisdiction_level: 'Federal'
  }, {
    agency_id: '2',
    agency_code: 'REV',
    agency_name: 'Revenue Authority',
    jurisdiction_level: 'Regional'
  }, {
    agency_id: '3',
    agency_code: 'CITY',
    agency_name: 'City Administration',
    jurisdiction_level: 'Municipal'
  }];
  const jurisdictionLevels: JurisdictionLevel[] = [{
    level_id: '1',
    level_name: 'National'
  }, {
    level_id: '2',
    level_name: 'Region'
  }, {
    level_id: '3',
    level_name: 'Zone'
  }, {
    level_id: '4',
    level_name: 'Woreda/City Admin'
  }, {
    level_id: '5',
    level_name: 'Kebele'
  }];
  const mockWorkflowDefinitions: WorkflowDefinition[] = [{
    workflow_def_id: 'WF001',
    workflow_code: 'STD_PERMIT_REVIEW',
    workflow_name: 'Standard Permit Review Process',
    description: 'Default workflow for permit applications',
    category: 'Permits & Licensing'
  }, {
    workflow_def_id: 'WF002',
    workflow_code: 'EXPEDITED_REVIEW',
    workflow_name: 'Expedited Application Review',
    description: 'Accelerated review process for urgent applications',
    category: 'Permits & Licensing'
  }, {
    workflow_def_id: 'WF003',
    workflow_code: 'COMPLEX_REVIEW',
    workflow_name: 'Complex Application Review',
    description: 'Extended review process for complex applications',
    category: 'Permits & Licensing'
  }];
  const mockWorkflowSteps: WorkflowStep[] = [{
    step_id: 'STEP001',
    sequence: 1,
    step_code: 'INITIAL_REVIEW',
    step_name: 'Initial Documentation Review',
    default_role: 'Documentation Officer',
    default_sla_days: 2
  }, {
    step_id: 'STEP002',
    sequence: 2,
    step_code: 'TECHNICAL_ASSESSMENT',
    step_name: 'Technical Assessment',
    default_role: 'Technical Officer',
    default_sla_days: 5
  }, {
    step_id: 'STEP003',
    sequence: 3,
    step_code: 'SUPERVISOR_REVIEW',
    step_name: 'Supervisory Review',
    default_role: 'Supervisor',
    default_sla_days: 3
  }, {
    step_id: 'STEP004',
    sequence: 4,
    step_code: 'FINAL_APPROVAL',
    step_name: 'Final Approval',
    default_role: 'Department Head',
    default_sla_days: 2
  }];
  const mockDocumentTypes: DocumentType[] = [{
    id: 'DOC001',
    name: 'Business License Certificate',
    description: 'Valid government-issued identification document',
    category: 'Licenses'
  }, {
    id: 'DOC002',
    name: 'Building Permit',
    description: 'Recent utility bill or lease agreement',
    category: 'Permits'
  }, {
    id: 'DOC003',
    name: 'Tax Clearance Certificate',
    description: 'Official business registration certificate',
    category: 'Certificates'
  }, {
    id: 'DOC004',
    name: 'Approval Letter',
    description: 'Current tax clearance certificate',
    category: 'Letters'
  }];
  const mockDocumentTemplates = [{
    id: 'TPL001',
    name: 'Standard Business License',
    documentTypeId: 'DOC001'
  }, {
    id: 'TPL002',
    name: 'Premium Business License',
    documentTypeId: 'DOC001'
  }, {
    id: 'TPL003',
    name: 'Standard Building Permit',
    documentTypeId: 'DOC002'
  }, {
    id: 'TPL004',
    name: 'Standard Tax Clearance',
    documentTypeId: 'DOC003'
  }];
  const mockCollectionOffices = [{
    id: 'OFF001',
    name: 'Main Municipal Office'
  }, {
    id: 'OFF002',
    name: 'Northern Branch Office'
  }, {
    id: 'OFF003',
    name: 'Eastern Service Center'
  }];
  const mockRevenueTypes = [{
    id: 'REV001',
    name: 'Application Fee',
    is_fee: true
  }, {
    id: 'REV002',
    name: 'Processing Fee',
    is_fee: true
  }, {
    id: 'REV003',
    name: 'Service Tax',
    is_tax: true
  }, {
    id: 'REV004',
    name: 'Expedited Processing Fee',
    is_fee: true
  }];
  const mockServiceFeeTypes = [{
    id: 'FEE001',
    name: 'Fixed Fee'
  }, {
    id: 'FEE002',
    name: 'Variable Rate'
  }, {
    id: 'FEE003',
    name: 'Per Unit Fee'
  }];
  const mockPaymentMethods = [{
    id: 'PAY001',
    name: 'Telebirr'
  }, {
    id: 'PAY002',
    name: 'CBE Birr'
  }, {
    id: 'PAY003',
    name: 'Bank Transfer'
  }, {
    id: 'PAY004',
    name: 'Card'
  }, {
    id: 'PAY005',
    name: 'Cash (at counter)'
  }];
  const mockNotificationEvents = [{
    id: 'EVT001',
    name: 'Application Submitted',
    isWorkflowStep: false
  }, {
    id: 'EVT002',
    name: 'Status Change: Pending Information',
    isWorkflowStep: false
  }, {
    id: 'EVT003',
    name: 'Payment Required',
    isWorkflowStep: false
  }, {
    id: 'EVT004',
    name: 'Document Review Complete',
    isWorkflowStep: true
  }, {
    id: 'EVT005',
    name: 'Final Approval',
    isWorkflowStep: true
  }];
  const mockMessageTemplates = [{
    id: 'TPL001',
    name: 'Application Confirmation - Email',
    channel: 'email'
  }, {
    id: 'TPL002',
    name: 'Application Confirmation - SMS',
    channel: 'sms'
  }, {
    id: 'TPL003',
    name: 'Information Request - Email',
    channel: 'email'
  }, {
    id: 'TPL004',
    name: 'Approval Notification - Portal',
    channel: 'portal'
  }];
  const mockRecipientGroups = [{
    id: 'RCP001',
    name: 'Applicant'
  }, {
    id: 'RCP002',
    name: 'Assigned Officer'
  }, {
    id: 'RCP003',
    name: 'Supervisor'
  }, {
    id: 'RCP004',
    name: 'Department Head'
  }];
  const validateForm = () => {
    const newErrors: Record<string, string> = {};
    if (!formData.serviceCode) {
      newErrors.serviceCode = 'Service Code is required';
    } else if (!/^[A-Z0-9_]+$/.test(formData.serviceCode)) {
      newErrors.serviceCode = 'Service Code must contain only uppercase letters, numbers, and underscores';
    }
    if (!formData.serviceName) {
      newErrors.serviceName = 'Service Name is required';
    }
    if (!formData.categoryId) {
      newErrors.categoryId = 'Category is required';
    }
    if (!formData.description || formData.description.length < 50) {
      newErrors.description = 'Description must be at least 50 characters';
    }
    if (formData.metadataSchema) {
      try {
        JSON.parse(formData.metadataSchema);
      } catch (e) {
        newErrors.metadataSchema = 'Invalid JSON format';
      }
    }
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };
  const getNextTab = (currentTab: TabId): TabId => {
    const tabOrder: TabId[] = ['core', 'agency', 'workflow', 'documents', 'fees', 'delivery', 'communications', 'review'];
    const currentIndex = tabOrder.indexOf(currentTab);
    return tabOrder[currentIndex + 1];
  };
  const tabs: Tab[] = [{
    id: 'core',
    name: 'Core Service Definition',
    description: 'Basic service information and configuration',
    canAccess: true
  }, {
    id: 'agency',
    name: 'Agency & Jurisdiction',
    description: 'Responsible departments and service scope',
    canAccess: isDraft
  }, {
    id: 'workflow',
    name: 'Workflow Configuration',
    description: 'Process steps and approval flows',
    canAccess: isDraft
  }, {
    id: 'documents',
    name: 'Document Requirements',
    description: 'Required documentation and attachments',
    canAccess: isDraft
  }, {
    id: 'fees',
    name: 'Fees & Payments',
    description: 'Service charges and payment settings',
    canAccess: isDraft
  }, {
    id: 'delivery',
    name: 'Service Delivery',
    description: 'Output and delivery configuration',
    canAccess: isDraft
  }, {
    id: 'communications',
    name: 'Communications',
    description: 'Notifications and messaging settings',
    canAccess: isDraft
  }, {
    id: 'review',
    name: 'Review & Activate',
    description: 'Final review and service activation',
    canAccess: isDraft
  }];
  return <div className="min-h-screen bg-gray-50" data-id="element-2205">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8" data-id="element-2206">
        {/* Breadcrumb */}
        <div className="flex items-center text-sm text-gray-500 mb-8" data-id="element-2207">
          <a href="/" className="hover:text-gray-700" data-id="element-2208">
            Dashboard
          </a>
          <ChevronRightIcon className="h-4 w-4 mx-2" data-id="element-2209" />
          <a href="/admin" className="hover:text-gray-700" data-id="element-2210">
            Administration
          </a>
          <ChevronRightIcon className="h-4 w-4 mx-2" data-id="element-2211" />
          <a href="/admin/services" className="hover:text-gray-700" data-id="element-2212">
            Services
          </a>
          <ChevronRightIcon className="h-4 w-4 mx-2" data-id="element-2213" />
          <span className="text-gray-900" data-id="element-2214">Create Service</span>
        </div>
        {/* Header */}
        <div className="mb-8 flex justify-between items-start" data-id="element-2215">
          <div data-id="element-2216">
            <h1 className="text-2xl font-bold text-gray-900" data-id="element-2217">
              Create New Service
            </h1>
            <p className="mt-1 text-sm text-gray-500" data-id="element-2218">
              Configure a new municipal service definition
            </p>
          </div>
          <div className="flex space-x-3" data-id="element-2219">
            <button className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50 flex items-center" onClick={() => setIsDraft(true)} data-id="element-2220">
              <SaveIcon className="h-4 w-4 mr-2" data-id="element-2221" />
              Save as Draft
            </button>
            <button className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50" data-id="element-2222">
              <XIcon className="h-4 w-4 mr-2" data-id="element-2223" />
              Cancel
            </button>
          </div>
        </div>
        {/* Tabs Navigation */}
        <div className="mb-8" data-id="element-2224">
          <nav className="flex space-x-4" aria-label="Tabs" data-id="element-2225">
            {tabs.map(tab => <button key={tab.id} onClick={() => tab.canAccess && setCurrentTab(tab.id)} className={`
                  px-3 py-2 text-sm font-medium rounded-md
                  ${currentTab === tab.id ? 'bg-blue-100 text-blue-700' : tab.canAccess ? 'text-gray-500 hover:text-gray-700' : 'text-gray-300 cursor-not-allowed'}
                `} data-id="element-2226">
                {tab.name}
              </button>)}
          </nav>
        </div>
        {/* Tab Description */}
        <div className="mb-8 bg-white p-4 rounded-lg border border-gray-200" data-id="element-2227">
          <h2 className="text-lg font-medium text-gray-900 mb-1" data-id="element-2228">
            {tabs.find(tab => tab.id === currentTab)?.name}
          </h2>
          <p className="text-sm text-gray-500" data-id="element-2229">
            {tabs.find(tab => tab.id === currentTab)?.description}
          </p>
        </div>
        {/* Tab Content */}
        <div className="bg-white rounded-lg border border-gray-200 p-6" data-id="element-2230">
          {/* Content will be added for each tab */}
          {currentTab === 'core' && <div className="space-y-6" data-id="element-2231">
              {/* Service ID */}
              <div data-id="element-2232">
                <label className="block text-sm font-medium text-gray-700" data-id="element-2233">
                  Service ID
                </label>
                <input type="text" value={formData.serviceId} disabled className="mt-1 block w-full px-3 py-2 bg-gray-100 border border-gray-300 rounded-md shadow-sm text-gray-500 sm:text-sm" data-id="element-2234" />
                <p className="mt-1 text-sm text-gray-500" data-id="element-2235">
                  Unique system identifier for this service. Generated
                  automatically.
                </p>
              </div>
              {/* Service Code */}
              <div data-id="element-2236">
                <label className="block text-sm font-medium text-gray-700" data-id="element-2237">
                  Service Code*
                </label>
                <div className="mt-1 relative" data-id="element-2238">
                  <input type="text" value={formData.serviceCode} onChange={e => {
                const value = e.target.value.toUpperCase();
                setFormData({
                  ...formData,
                  serviceCode: value
                });
                // Simulate checking uniqueness
                setIsCheckingCode(true);
                setTimeout(() => {
                  setIsCodeUnique(value.length > 0);
                  setIsCheckingCode(false);
                }, 500);
              }} placeholder="e.g., LPROP_NEW_TITLE_RES" className={`block w-full px-3 py-2 border ${errors.serviceCode ? 'border-red-300' : 'border-gray-300'} rounded-md shadow-sm focus:ring-blue-500 focus:border-blue-500 sm:text-sm`} data-id="element-2239" />
                  <div className="absolute inset-y-0 right-0 flex items-center pr-3" data-id="element-2240">
                    {isCheckingCode && <Loader2Icon className="h-4 w-4 text-gray-400 animate-spin" data-id="element-2241" />}
                    {!isCheckingCode && isCodeUnique && <CheckCircleIcon className="h-4 w-4 text-green-500" data-id="element-2242" />}
                    {!isCheckingCode && isCodeUnique === false && <AlertCircleIcon className="h-4 w-4 text-red-500" data-id="element-2243" />}
                  </div>
                </div>
                {errors.serviceCode && <p className="mt-1 text-sm text-red-600" data-id="element-2244">
                    {errors.serviceCode}
                  </p>}
                <p className="mt-1 text-sm text-gray-500" data-id="element-2245">
                  Enter a short, unique, uppercase, machine-readable code
                  (letters, numbers, underscores only, max 100 chars).
                </p>
              </div>
              {/* Service Name */}
              <div data-id="element-2246">
                <label className="block text-sm font-medium text-gray-700" data-id="element-2247">
                  Service Name*
                </label>
                <input type="text" value={formData.serviceName} onChange={e => setFormData({
              ...formData,
              serviceName: e.target.value
            })} placeholder="e.g., Application for New Land Title (Residential Properties)" className={`mt-1 block w-full px-3 py-2 border ${errors.serviceName ? 'border-red-300' : 'border-gray-300'} rounded-md shadow-sm focus:ring-blue-500 focus:border-blue-500 sm:text-sm`} data-id="element-2248" />
                {errors.serviceName && <p className="mt-1 text-sm text-red-600" data-id="element-2249">
                    {errors.serviceName}
                  </p>}
                <p className="mt-1 text-sm text-gray-500" data-id="element-2250">
                  Enter the official, user-facing name of the service as it will
                  appear to citizens and officers.
                </p>
              </div>
              {/* Category */}
              <div data-id="element-2251">
                <label className="block text-sm font-medium text-gray-700" data-id="element-2252">
                  Service Category*
                </label>
                <select value={formData.categoryId} onChange={e => setFormData({
              ...formData,
              categoryId: e.target.value
            })} className={`mt-1 block w-full px-3 py-2 border ${errors.categoryId ? 'border-red-300' : 'border-gray-300'} rounded-md shadow-sm focus:ring-blue-500 focus:border-blue-500 sm:text-sm`} data-id="element-2253">
                  <option value="" data-id="element-2254">Select a category</option>
                  {categories.map(category => <option key={category.category_id} value={category.category_id} data-id="element-2255">
                      {category.category_name}
                    </option>)}
                </select>
                {errors.categoryId && <p className="mt-1 text-sm text-red-600" data-id="element-2256">
                    {errors.categoryId}
                  </p>}
                <p className="mt-1 text-sm text-gray-500" data-id="element-2257">
                  Select the primary administrative or functional category this
                  service belongs to.
                </p>
              </div>
              {/* Description */}
              <div data-id="element-2258">
                <label className="block text-sm font-medium text-gray-700" data-id="element-2259">
                  Description*
                </label>
                <textarea value={formData.description} onChange={e => setFormData({
              ...formData,
              description: e.target.value
            })} rows={4} className={`mt-1 block w-full px-3 py-2 border ${errors.description ? 'border-red-300' : 'border-gray-300'} rounded-md shadow-sm focus:ring-blue-500 focus:border-blue-500 sm:text-sm`} data-id="element-2260" />
                {errors.description && <p className="mt-1 text-sm text-red-600" data-id="element-2261">
                    {errors.description}
                  </p>}
                <p className="mt-1 text-sm text-gray-500" data-id="element-2262">
                  Provide a comprehensive public-facing description: purpose,
                  eligibility, outcome, key steps involved.
                </p>
              </div>
              {/* Target Audience */}
              <div data-id="element-2263">
                <label className="block text-sm font-medium text-gray-700" data-id="element-2264">
                  Target Audience
                </label>
                <div className="mt-2 space-y-2" data-id="element-2265">
                  {audienceTypes.map(type => <label key={type} className="inline-flex items-center mr-4" data-id="element-2266">
                      <input type="checkbox" checked={formData.targetAudiences.includes(type)} onChange={e => {
                  const newAudiences = e.target.checked ? [...formData.targetAudiences, type] : formData.targetAudiences.filter(t => t !== type);
                  setFormData({
                    ...formData,
                    targetAudiences: newAudiences
                  });
                }} className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded" data-id="element-2267" />
                      <span className="ml-2 text-sm text-gray-700" data-id="element-2268">{type}</span>
                    </label>)}
                </div>
              </div>
              {/* Search Tags */}
              <div data-id="element-2269">
                <label className="block text-sm font-medium text-gray-700" data-id="element-2270">
                  Keywords/Tags
                </label>
                <input type="text" placeholder="Type and press Enter to add tags" onKeyDown={e => {
              if (e.key === 'Enter' && e.currentTarget.value) {
                e.preventDefault();
                const newTag = e.currentTarget.value.trim();
                if (newTag && !formData.searchTags.includes(newTag)) {
                  setFormData({
                    ...formData,
                    searchTags: [...formData.searchTags, newTag]
                  });
                  e.currentTarget.value = '';
                }
              }
            }} className="mt-1 block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:ring-blue-500 focus:border-blue-500 sm:text-sm" data-id="element-2271" />
                <div className="mt-2 flex flex-wrap gap-2" data-id="element-2272">
                  {formData.searchTags.map((tag, index) => <span key={index} className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-blue-100 text-blue-800" data-id="element-2273">
                      {tag}
                      <button type="button" onClick={() => {
                  setFormData({
                    ...formData,
                    searchTags: formData.searchTags.filter((_, i) => i !== index)
                  });
                }} className="ml-1 text-blue-600 hover:text-blue-800" data-id="element-2274">
                        ×
                      </button>
                    </span>)}
                </div>
              </div>
              {/* Metadata Schema */}
              <div data-id="element-2275">
                <label className="block text-sm font-medium text-gray-700" data-id="element-2276">
                  Metadata Schema (JSON)
                </label>
                <textarea value={formData.metadataSchema} onChange={e => setFormData({
              ...formData,
              metadataSchema: e.target.value
            })} rows={4} placeholder="{}" className={`mt-1 block w-full px-3 py-2 border ${errors.metadataSchema ? 'border-red-300' : 'border-gray-300'} rounded-md shadow-sm focus:ring-blue-500 focus:border-blue-500 sm:text-sm font-mono`} data-id="element-2277" />
                {errors.metadataSchema && <p className="mt-1 text-sm text-red-600" data-id="element-2278">
                    {errors.metadataSchema}
                  </p>}
                <p className="mt-1 text-sm text-gray-500" data-id="element-2279">
                  Define a JSON Schema for additional, service-specific data
                  fields.
                </p>
              </div>
              {/* Is Active */}
              <div className="flex items-center" data-id="element-2280">
                <button type="button" onClick={() => setFormData({
              ...formData,
              isActive: !formData.isActive
            })} className={`${formData.isActive ? 'bg-blue-600' : 'bg-gray-200'} relative inline-flex flex-shrink-0 h-6 w-11 border-2 border-transparent rounded-full cursor-pointer transition-colors ease-in-out duration-200 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500`} data-id="element-2281">
                  <span className={`${formData.isActive ? 'translate-x-5' : 'translate-x-0'} pointer-events-none inline-block h-5 w-5 rounded-full bg-white shadow transform ring-0 transition ease-in-out duration-200`} data-id="element-2282" />
                </button>
                <span className="ml-3" data-id="element-2283">
                  <span className="text-sm font-medium text-gray-900" data-id="element-2284">
                    Service is Active
                  </span>
                  <span className="text-sm text-gray-500" data-id="element-2285">
                    {' '}
                    and Available for New Requests
                  </span>
                </span>
              </div>
              {/* Internal Notes */}
              <div data-id="element-2286">
                <label className="block text-sm font-medium text-gray-700" data-id="element-2287">
                  Internal Notes
                </label>
                <textarea value={formData.internalNotes} onChange={e => setFormData({
              ...formData,
              internalNotes: e.target.value
            })} rows={3} className="mt-1 block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:ring-blue-500 focus:border-blue-500 sm:text-sm" data-id="element-2288" />
                <p className="mt-1 text-sm text-gray-500" data-id="element-2289">
                  Internal notes for administrators regarding this service
                  definition.
                </p>
              </div>
            </div>}
          {currentTab === 'agency' && <div className="space-y-8" data-id="element-2290">
              <div className="border-b border-gray-200 pb-8" data-id="element-2291">
                <h3 className="text-lg font-medium text-gray-900 mb-6" data-id="element-2292">
                  Primary Responsible Agency & Oversight
                </h3>
                {/* Primary Responsible Agency */}
                <div className="space-y-6" data-id="element-2293">
                  <div data-id="element-2294">
                    <label className="block text-sm font-medium text-gray-700" data-id="element-2295">
                      Primary Responsible Agency*
                    </label>
                    <select value={agencyFormData.responsibleAgencyId} onChange={e => setAgencyFormData({
                  ...agencyFormData,
                  responsibleAgencyId: e.target.value
                })} className="mt-1 block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:ring-blue-500 focus:border-blue-500 sm:text-sm" data-id="element-2296">
                      <option value="" data-id="element-2297">Select an agency</option>
                      {agencies.map(agency => <option key={agency.agency_id} value={agency.agency_id} data-id="element-2298">
                          {agency.agency_code} - {agency.agency_name}
                        </option>)}
                    </select>
                    <p className="mt-1 text-sm text-gray-500" data-id="element-2299">
                      Select the main government agency directly accountable for
                      this service.
                    </p>
                  </div>
                  {/* Agency's Operational Level */}
                  <div data-id="element-2300">
                    <label className="block text-sm font-medium text-gray-700" data-id="element-2301">
                      Agency's Operational Level
                    </label>
                    <input type="text" value={agencies.find(a => a.agency_id === agencyFormData.responsibleAgencyId)?.jurisdiction_level || ''} disabled className="mt-1 block w-full px-3 py-2 bg-gray-100 border border-gray-300 rounded-md shadow-sm text-gray-500 sm:text-sm" data-id="element-2302" />
                    <p className="mt-1 text-sm text-gray-500" data-id="element-2303">
                      This indicates the primary administrative level at which
                      the selected agency operates.
                    </p>
                  </div>
                  {/* Supporting Agencies */}
                  <div data-id="element-2304">
                    <label className="block text-sm font-medium text-gray-700" data-id="element-2305">
                      Supporting Agencies
                    </label>
                    <select multiple value={agencyFormData.supportingAgencies} onChange={e => {
                  const values = Array.from(e.target.selectedOptions, option => option.value);
                  setAgencyFormData({
                    ...agencyFormData,
                    supportingAgencies: values
                  });
                }} className="mt-1 block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:ring-blue-500 focus:border-blue-500 sm:text-sm" data-id="element-2306">
                      {agencies.filter(a => a.agency_id !== agencyFormData.responsibleAgencyId).map(agency => <option key={agency.agency_id} value={agency.agency_id} data-id="element-2307">
                            {agency.agency_code} - {agency.agency_name}
                          </option>)}
                    </select>
                    <p className="mt-1 text-sm text-gray-500" data-id="element-2308">
                      Select other agencies involved in review, approval, or
                      support for this service.
                    </p>
                  </div>
                </div>
              </div>
              {/* Jurisdictional Scope */}
              <div className="space-y-6" data-id="element-2309">
                <h3 className="text-lg font-medium text-gray-900 mb-6" data-id="element-2310">
                  Service Applicability & Availability
                </h3>
                {/* Default Jurisdiction Level */}
                <div data-id="element-2311">
                  <label className="block text-sm font-medium text-gray-700" data-id="element-2312">
                    Default Applicable Jurisdiction Level*
                  </label>
                  <select value={agencyFormData.jurisdictionLevelId} onChange={e => setAgencyFormData({
                ...agencyFormData,
                jurisdictionLevelId: e.target.value
              })} className="mt-1 block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:ring-blue-500 focus:border-blue-500 sm:text-sm" data-id="element-2313">
                    <option value="" data-id="element-2314">Select a level</option>
                    {jurisdictionLevels.map(level => <option key={level.level_id} value={level.level_id} data-id="element-2315">
                        {level.level_name}
                      </option>)}
                  </select>
                  <p className="mt-1 text-sm text-gray-500" data-id="element-2316">
                    Select the general lowest administrative level at which this
                    service is typically offered.
                  </p>
                </div>
                {/* Jurisdiction Overrides */}
                <div data-id="element-2317">
                  <div className="flex justify-between items-center mb-4" data-id="element-2318">
                    <h4 className="text-sm font-medium text-gray-900" data-id="element-2319">
                      Jurisdictional Availability Overrides
                    </h4>
                    <button onClick={() => {
                  setCurrentOverride(null);
                  setShowOverrideModal(true);
                }} className="inline-flex items-center px-3 py-2 border border-transparent text-sm font-medium rounded-md shadow-sm text-white bg-blue-600 hover:bg-blue-700" data-id="element-2320">
                      <PlusIcon className="h-4 w-4 mr-2" data-id="element-2321" />
                      Add Override
                    </button>
                  </div>
                  {/* Overrides Table */}
                  <div className="mt-4 border border-gray-200 rounded-lg overflow-hidden" data-id="element-2322">
                    <table className="min-w-full divide-y divide-gray-200" data-id="element-2323">
                      <thead className="bg-gray-50" data-id="element-2324">
                        <tr data-id="element-2325">
                          <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-2326">
                            Jurisdiction Path
                          </th>
                          <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-2327">
                            Level
                          </th>
                          <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-2328">
                            Availability
                          </th>
                          <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-2329">
                            Cascades
                          </th>
                          <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-2330">
                            Actions
                          </th>
                        </tr>
                      </thead>
                      <tbody className="bg-white divide-y divide-gray-200" data-id="element-2331">
                        {agencyFormData.overrides.map(override => <tr key={override.id} data-id="element-2332">
                            <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900" data-id="element-2333">
                              {override.jurisdiction}
                            </td>
                            <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500" data-id="element-2334">
                              {override.level}
                            </td>
                            <td className="px-6 py-4 whitespace-nowrap" data-id="element-2335">
                              <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${override.isAvailable ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'}`} data-id="element-2336">
                                {override.isAvailable ? 'Available' : 'Unavailable'}
                              </span>
                            </td>
                            <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500" data-id="element-2337">
                              {override.cascades ? 'Yes' : 'No'}
                            </td>
                            <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500" data-id="element-2338">
                              <button onClick={() => {
                          setCurrentOverride(override);
                          setShowOverrideModal(true);
                        }} className="text-blue-600 hover:text-blue-900 mr-3" data-id="element-2339">
                                <PencilIcon className="h-4 w-4" data-id="element-2340" />
                              </button>
                              <button onClick={() => {
                          setAgencyFormData({
                            ...agencyFormData,
                            overrides: agencyFormData.overrides.filter(o => o.id !== override.id)
                          });
                        }} className="text-red-600 hover:text-red-900" data-id="element-2341">
                                <TrashIcon className="h-4 w-4" data-id="element-2342" />
                              </button>
                            </td>
                          </tr>)}
                      </tbody>
                    </table>
                  </div>
                </div>
              </div>
            </div>}
          {currentTab === 'workflow' && <div className="space-y-8" data-id="element-2343">
              {/* Section 1: Primary Workflow Assignment */}
              <div className="border-b border-gray-200 pb-8" data-id="element-2344">
                <h3 className="text-lg font-medium text-gray-900 mb-6" data-id="element-2345">
                  Primary Workflow Assignment
                </h3>
                <div className="space-y-4" data-id="element-2346">
                  <div data-id="element-2347">
                    <label className="block text-sm font-medium text-gray-700" data-id="element-2348">
                      Assign Primary Workflow Definition*
                    </label>
                    <div className="mt-1" data-id="element-2349">
                      <select value={selectedWorkflowId} onChange={e => {
                    setSelectedWorkflowId(e.target.value);
                    setWorkflowSteps(mockWorkflowSteps);
                  }} className="mt-1 block w-full pl-3 pr-10 py-2 text-base border-gray-300 focus:outline-none focus:ring-blue-500 focus:border-blue-500 sm:text-sm rounded-md" data-id="element-2350">
                        <option value="" data-id="element-2351">Select a workflow...</option>
                        {mockWorkflowDefinitions.map(wf => <option key={wf.workflow_def_id} value={wf.workflow_def_id} data-id="element-2352">
                            {wf.workflow_code} - {wf.workflow_name}
                          </option>)}
                      </select>
                    </div>
                    <p className="mt-2 text-sm text-gray-500" data-id="element-2353">
                      Select the standard sequence of steps, reviews, and
                      approvals that this service will follow. This dictates the
                      lifecycle of applications for this service.
                    </p>
                  </div>
                  <button className="inline-flex items-center px-4 py-2 border border-gray-300 rounded-md shadow-sm text-sm font-medium text-gray-700 bg-white hover:bg-gray-50" data-id="element-2354">
                    <PlusIcon className="h-4 w-4 mr-2" data-id="element-2355" />
                    Create New Workflow Definition
                  </button>
                </div>
              </div>
              {/* Section 2: Workflow Steps Configuration */}
              {selectedWorkflowId && <div className="border-b border-gray-200 pb-8" data-id="element-2356">
                  <h3 className="text-lg font-medium text-gray-900 mb-6" data-id="element-2357">
                    Workflow Steps Configuration & SLAs
                  </h3>
                  <div className="bg-gray-50 p-4 rounded-md mb-6" data-id="element-2358">
                    <p className="text-sm text-gray-600" data-id="element-2359">
                      The selected workflow defines a standard set of steps.
                      Below, you can review these steps and, if necessary,
                      override the default assigned role or Service Level
                      Agreement (SLA) duration specifically for this service.
                    </p>
                  </div>
                  {/* Steps Table */}
                  <div className="mt-4 flex flex-col" data-id="element-2360">
                    <div className="-my-2 -mx-4 overflow-x-auto sm:-mx-6 lg:-mx-8" data-id="element-2361">
                      <div className="inline-block min-w-full py-2 align-middle md:px-6 lg:px-8" data-id="element-2362">
                        <div className="overflow-hidden shadow ring-1 ring-black ring-opacity-5 md:rounded-lg" data-id="element-2363">
                          <table className="min-w-full divide-y divide-gray-300" data-id="element-2364">
                            <thead className="bg-gray-50" data-id="element-2365">
                              <tr data-id="element-2366">
                                <th className="px-3 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-2367">
                                  Sequence
                                </th>
                                <th className="px-3 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-2368">
                                  Step Name
                                </th>
                                <th className="px-3 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-2369">
                                  Default Role
                                </th>
                                <th className="px-3 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-2370">
                                  Role Override
                                </th>
                                <th className="px-3 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-2371">
                                  Default SLA
                                </th>
                                <th className="px-3 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-2372">
                                  SLA Override
                                </th>
                                <th className="px-3 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-2373">
                                  Actions
                                </th>
                              </tr>
                            </thead>
                            <tbody className="bg-white divide-y divide-gray-200" data-id="element-2374">
                              {workflowSteps.map(step => <tr key={step.step_id} data-id="element-2375">
                                  <td className="px-3 py-4 whitespace-nowrap text-sm text-gray-500" data-id="element-2376">
                                    {step.sequence}
                                  </td>
                                  <td className="px-3 py-4 whitespace-nowrap text-sm text-gray-900" data-id="element-2377">
                                    {step.step_name}
                                  </td>
                                  <td className="px-3 py-4 whitespace-nowrap text-sm text-gray-500" data-id="element-2378">
                                    {step.default_role}
                                  </td>
                                  <td className="px-3 py-4 whitespace-nowrap text-sm text-gray-500" data-id="element-2379">
                                    {step.overridden_role || '-'}
                                  </td>
                                  <td className="px-3 py-4 whitespace-nowrap text-sm text-gray-500" data-id="element-2380">
                                    {step.default_sla_days} days
                                  </td>
                                  <td className="px-3 py-4 whitespace-nowrap text-sm text-gray-500" data-id="element-2381">
                                    {step.overridden_sla_days ? `${step.overridden_sla_days} days` : '-'}
                                  </td>
                                  <td className="px-3 py-4 whitespace-nowrap text-sm text-gray-500" data-id="element-2382">
                                    <button onClick={() => {
                              setSelectedStep(step);
                              setShowStepModal(true);
                            }} className="text-blue-600 hover:text-blue-900" data-id="element-2383">
                                      Configure
                                    </button>
                                  </td>
                                </tr>)}
                            </tbody>
                          </table>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>}
              {/* Section 3: Conditional Workflow Logic */}
              <div className="pt-8" data-id="element-2384">
                <div className="flex justify-between items-center mb-6" data-id="element-2385">
                  <div data-id="element-2386">
                    <h3 className="text-lg font-medium text-gray-900" data-id="element-2387">
                      Conditional Workflow Logic
                    </h3>
                    <p className="mt-1 text-sm text-gray-500" data-id="element-2388">
                      Define rules to trigger different workflows based on
                      application data. Rules are evaluated in priority order.
                    </p>
                  </div>
                  <button className="inline-flex items-center px-4 py-2 border border-gray-300 rounded-md shadow-sm text-sm font-medium text-gray-700 bg-white hover:bg-gray-50" data-id="element-2389">
                    <PlusIcon className="h-4 w-4 mr-2" data-id="element-2390" />
                    Add Conditional Rule
                  </button>
                </div>
                {conditionalRules.length === 0 ? <div className="text-center py-12 bg-gray-50 rounded-lg border-2 border-dashed border-gray-300" data-id="element-2391">
                    <p className="text-sm text-gray-500" data-id="element-2392">
                      No conditional workflow rules defined yet.
                    </p>
                  </div> : <div className="space-y-4" data-id="element-2393">
                    {/* Rule cards would go here */}
                  </div>}
              </div>
            </div>}
          {currentTab === 'documents' && <div className="space-y-8" data-id="element-2394">
              {/* Header and Instructions */}
              <div className="bg-gray-50 p-4 rounded-md mb-6" data-id="element-2395">
                <h3 className="text-lg font-medium text-gray-900 mb-2" data-id="element-2396">
                  Document Requirements Configuration
                </h3>
                <p className="text-sm text-gray-600" data-id="element-2397">
                  Specify the standard documents applicants must typically
                  provide for this service. These will be presented as a
                  checklist during application and can be used for automated
                  completeness checks.
                </p>
              </div>
              {/* Filters */}
              <div className="flex items-center justify-between mb-4" data-id="element-2398">
                <div className="flex space-x-4" data-id="element-2399">
                  <select className="block w-auto px-3 py-2 border border-gray-300 rounded-md text-sm" value={documentFilter.mandatory} onChange={e => setDocumentFilter({
                ...documentFilter,
                mandatory: e.target.value
              })} data-id="element-2400">
                    <option value="all" data-id="element-2401">All Requirements</option>
                    <option value="mandatory" data-id="element-2402">Mandatory Only</option>
                    <option value="optional" data-id="element-2403">Optional Only</option>
                  </select>
                  <select className="block w-auto px-3 py-2 border border-gray-300 rounded-md text-sm" value={documentFilter.type} onChange={e => setDocumentFilter({
                ...documentFilter,
                type: e.target.value
              })} data-id="element-2404">
                    <option value="all" data-id="element-2405">All Document Types</option>
                    {Array.from(new Set(mockDocumentTypes.map(dt => dt.category))).map(category => <option key={category} value={category} data-id="element-2406">
                        {category}
                      </option>)}
                  </select>
                </div>
                <button onClick={() => {
              setSelectedDocument(null);
              setShowDocumentModal(true);
            }} className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md shadow-sm text-white bg-blue-600 hover:bg-blue-700" data-id="element-2407">
                  <PlusIcon className="h-4 w-4 mr-2" data-id="element-2408" />
                  Add Document Requirement
                </button>
              </div>
              {/* Requirements Table */}
              <div className="bg-white shadow ring-1 ring-black ring-opacity-5 rounded-lg overflow-hidden" data-id="element-2409">
                <table className="min-w-full divide-y divide-gray-300" data-id="element-2410">
                  <thead className="bg-gray-50" data-id="element-2411">
                    <tr data-id="element-2412">
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-2413">
                        Document Type
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-2414">
                        Mandatory
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-2415">
                        Condition Logic
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-2416">
                        Target Jurisdiction
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-2417">
                        Applicant Types
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-2418">
                        Status
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-2419">
                        Actions
                      </th>
                    </tr>
                  </thead>
                  <tbody className="bg-white divide-y divide-gray-200" data-id="element-2420">
                    {documentRequirements.length === 0 ? <tr data-id="element-2421">
                        <td colSpan={7} className="px-6 py-8 text-center text-sm text-gray-500" data-id="element-2422">
                          No document requirements defined yet. Click "Add
                          Document Requirement" to get started.
                        </td>
                      </tr> : documentRequirements.map(req => <tr key={req.id} data-id="element-2423">
                          <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900" data-id="element-2424">
                            {req.documentTypeName}
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500" data-id="element-2425">
                            <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${req.isMandatory ? 'bg-red-100 text-red-800' : 'bg-gray-100 text-gray-800'}`} data-id="element-2426">
                              {req.isMandatory ? 'Required' : 'Optional'}
                            </span>
                          </td>
                          <td className="px-6 py-4 text-sm text-gray-500" data-id="element-2427">
                            {req.conditionLogic || '-'}
                          </td>
                          <td className="px-6 py-4 text-sm text-gray-500" data-id="element-2428">
                            {req.targetJurisdiction || 'All'}
                          </td>
                          <td className="px-6 py-4 text-sm text-gray-500" data-id="element-2429">
                            {req.targetApplicantTypes.length > 0 ? req.targetApplicantTypes.join(', ') : 'All'}
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap" data-id="element-2430">
                            <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${req.isActive ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'}`} data-id="element-2431">
                              {req.isActive ? 'Active' : 'Inactive'}
                            </span>
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500" data-id="element-2432">
                            <button onClick={() => {
                      setSelectedDocument(req);
                      setShowDocumentModal(true);
                    }} className="text-blue-600 hover:text-blue-900 mr-3" data-id="element-2433">
                              <PencilIcon className="h-4 w-4" data-id="element-2434" />
                            </button>
                            <button onClick={() => {
                      setDocumentRequirements(documentRequirements.filter(d => d.id !== req.id));
                    }} className="text-red-600 hover:text-red-900" data-id="element-2435">
                              <TrashIcon className="h-4 w-4" data-id="element-2436" />
                            </button>
                          </td>
                        </tr>)}
                  </tbody>
                </table>
              </div>
            </div>}
          {currentTab === 'fees' && <div className="space-y-8" data-id="element-2437">
              {/* Section 1: Service Tariffs */}
              <div className="border-b border-gray-200 pb-8" data-id="element-2438">
                <h3 className="text-lg font-medium text-gray-900 mb-4" data-id="element-2439">
                  Service Tariffs Configuration
                </h3>
                <div className="bg-gray-50 p-4 rounded-md mb-6" data-id="element-2440">
                  <p className="text-sm text-gray-600" data-id="element-2441">
                    Define all potential costs associated with this service.
                    Tariffs can be specific to jurisdictions, applicant types,
                    conditions, or service variations.
                  </p>
                </div>
                {/* Tariffs Table */}
                <div className="mt-4" data-id="element-2442">
                  <div className="flex justify-end mb-4" data-id="element-2443">
                    <button onClick={() => {
                  setSelectedTariff(null);
                  setShowTariffModal(true);
                }} className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md shadow-sm text-white bg-blue-600 hover:bg-blue-700" data-id="element-2444">
                      <PlusIcon className="h-4 w-4 mr-2" data-id="element-2445" />
                      Add Tariff
                    </button>
                  </div>
                  <div className="bg-white shadow ring-1 ring-black ring-opacity-5 rounded-lg overflow-hidden" data-id="element-2446">
                    <table className="min-w-full divide-y divide-gray-300" data-id="element-2447">
                      <thead className="bg-gray-50" data-id="element-2448">
                        <tr data-id="element-2449">
                          <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-2450">
                            Description
                          </th>
                          <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-2451">
                            Revenue Type
                          </th>
                          <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-2452">
                            Amount/Rate
                          </th>
                          <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-2453">
                            Jurisdiction
                          </th>
                          <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-2454">
                            Effective Period
                          </th>
                          <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-2455">
                            Status
                          </th>
                          <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-2456">
                            Actions
                          </th>
                        </tr>
                      </thead>
                      <tbody className="bg-white divide-y divide-gray-200" data-id="element-2457">
                        {tariffs.length === 0 ? <tr data-id="element-2458">
                            <td colSpan={7} className="px-6 py-8 text-center text-sm text-gray-500" data-id="element-2459">
                              No tariffs defined yet. Click "Add Tariff" to get
                              started.
                            </td>
                          </tr> : tariffs.map(tariff => <tr key={tariff.id} data-id="element-2460">
                              <td className="px-6 py-4 text-sm text-gray-900" data-id="element-2461">
                                {tariff.description}
                              </td>
                              <td className="px-6 py-4 text-sm text-gray-500" data-id="element-2462">
                                {tariff.revenueType}
                              </td>
                              <td className="px-6 py-4 text-sm text-gray-900" data-id="element-2463">
                                {tariff.amount} {tariff.currency}
                                {tariff.unit && ` per ${tariff.unit}`}
                              </td>
                              <td className="px-6 py-4 text-sm text-gray-500" data-id="element-2464">
                                {tariff.applicableJurisdiction || 'All'}
                              </td>
                              <td className="px-6 py-4 text-sm text-gray-500" data-id="element-2465">
                                {tariff.effectiveFrom}
                                {tariff.effectiveUntil && ` to ${tariff.effectiveUntil}`}
                              </td>
                              <td className="px-6 py-4 whitespace-nowrap" data-id="element-2466">
                                <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${tariff.isActive ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'}`} data-id="element-2467">
                                  {tariff.isActive ? 'Active' : 'Inactive'}
                                </span>
                              </td>
                              <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500" data-id="element-2468">
                                <button onClick={() => {
                          setSelectedTariff(tariff);
                          setShowTariffModal(true);
                        }} className="text-blue-600 hover:text-blue-900 mr-3" data-id="element-2469">
                                  <PencilIcon className="h-4 w-4" data-id="element-2470" />
                                </button>
                                <button onClick={() => {
                          setTariffs(tariffs.filter(t => t.id !== tariff.id));
                        }} className="text-red-600 hover:text-red-900" data-id="element-2471">
                                  <TrashIcon className="h-4 w-4" data-id="element-2472" />
                                </button>
                              </td>
                            </tr>)}
                      </tbody>
                    </table>
                  </div>
                </div>
              </div>
              {/* Section 2: Payment Configuration */}
              <div className="space-y-6" data-id="element-2473">
                <h3 className="text-lg font-medium text-gray-900" data-id="element-2474">
                  Payment Configuration
                </h3>
                {/* Payment Required */}
                <div data-id="element-2475">
                  <label className="text-sm font-medium text-gray-700" data-id="element-2476">
                    Payment Required for this Service?*
                  </label>
                  <div className="mt-2 space-y-2" data-id="element-2477">
                    {['always', 'never', 'conditional'].map(option => <label key={option} className="flex items-center" data-id="element-2478">
                        <input type="radio" value={option} checked={paymentConfig.paymentRequired === option} onChange={e => setPaymentConfig({
                    ...paymentConfig,
                    paymentRequired: e.target.value as 'always' | 'never' | 'conditional'
                  })} className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300" data-id="element-2479" />
                        <span className="ml-2 text-sm text-gray-700" data-id="element-2480">
                          {option === 'always' ? 'Yes, always' : option === 'never' ? 'No, service is free' : 'Conditional based on rules'}
                        </span>
                      </label>)}
                  </div>
                </div>
                {/* Conditional Payment Logic */}
                {paymentConfig.paymentRequired === 'conditional' && <div data-id="element-2481">
                    <label className="block text-sm font-medium text-gray-700" data-id="element-2482">
                      Payment Condition Logic
                    </label>
                    <textarea value={paymentConfig.paymentConditionLogic} onChange={e => setPaymentConfig({
                ...paymentConfig,
                paymentConditionLogic: e.target.value
              })} rows={3} className="mt-1 block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-blue-500 focus:border-blue-500 sm:text-sm" placeholder="e.g., IF metadata.application_type = 'Express' THEN payment_required=TRUE" data-id="element-2483" />
                  </div>}
                {/* Payment Due Timing */}
                {paymentConfig.paymentRequired !== 'never' && <div data-id="element-2484">
                    <label className="block text-sm font-medium text-gray-700" data-id="element-2485">
                      When is Payment Due?*
                    </label>
                    <select value={paymentConfig.paymentDueTrigger} onChange={e => setPaymentConfig({
                ...paymentConfig,
                paymentDueTrigger: e.target.value
              })} className="mt-1 block w-full pl-3 pr-10 py-2 text-base border-gray-300 focus:outline-none focus:ring-blue-500 focus:border-blue-500 sm:text-sm rounded-md" data-id="element-2486">
                      <option value="submission" data-id="element-2487">
                        Upon Application Submission
                      </option>
                      <option value="review" data-id="element-2488">Before Specialist Review</option>
                      <option value="approval" data-id="element-2489">Before Final Approval</option>
                      <option value="completion" data-id="element-2490">
                        After Service Completion
                      </option>
                      <option value="billing" data-id="element-2491">After Bill Generation</option>
                      <option value="other" data-id="element-2492">Other (Specify)</option>
                    </select>
                    {paymentConfig.paymentDueTrigger === 'other' && <input type="text" value={paymentConfig.customPaymentDueTrigger} onChange={e => setPaymentConfig({
                ...paymentConfig,
                customPaymentDueTrigger: e.target.value
              })} className="mt-2 block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-blue-500 focus:border-blue-500 sm:text-sm" placeholder="Specify custom payment due trigger" data-id="element-2493" />}
                  </div>}
                {/* Allowed Payment Methods */}
                {paymentConfig.paymentRequired !== 'never' && <div data-id="element-2494">
                    <label className="block text-sm font-medium text-gray-700" data-id="element-2495">
                      Allowed Payment Methods
                    </label>
                    <p className="mt-1 text-sm text-gray-500" data-id="element-2496">
                      Leave unchecked for system defaults. Select to
                      restrict/expand options for this service.
                    </p>
                    <div className="mt-2 space-y-2" data-id="element-2497">
                      {mockPaymentMethods.map(method => <label key={method.id} className="flex items-center" data-id="element-2498">
                          <input type="checkbox" checked={paymentConfig.allowedPaymentMethods.includes(method.id)} onChange={e => {
                    const methods = e.target.checked ? [...paymentConfig.allowedPaymentMethods, method.id] : paymentConfig.allowedPaymentMethods.filter(id => id !== method.id);
                    setPaymentConfig({
                      ...paymentConfig,
                      allowedPaymentMethods: methods
                    });
                  }} className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded" data-id="element-2499" />
                          <span className="ml-2 text-sm text-gray-700" data-id="element-2500">
                            {method.name}
                          </span>
                        </label>)}
                    </div>
                  </div>}
                {/* Payment Instructions */}
                {paymentConfig.paymentRequired !== 'never' && <div data-id="element-2501">
                    <label className="block text-sm font-medium text-gray-700" data-id="element-2502">
                      Payment Instructions (Public Facing)
                    </label>
                    <textarea value={paymentConfig.paymentInstructions} onChange={e => setPaymentConfig({
                ...paymentConfig,
                paymentInstructions: e.target.value
              })} rows={4} className="mt-1 block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-blue-500 focus:border-blue-500 sm:text-sm" placeholder="Specific payment instructions for applicants (e.g., bank details, reference format)" data-id="element-2503" />
                  </div>}
                {/* Internal Notes */}
                {paymentConfig.paymentRequired !== 'never' && <div data-id="element-2504">
                    <label className="block text-sm font-medium text-gray-700" data-id="element-2505">
                      Internal Payment Processing Notes
                    </label>
                    <textarea value={paymentConfig.internalNotes} onChange={e => setPaymentConfig({
                ...paymentConfig,
                internalNotes: e.target.value
              })} rows={3} className="mt-1 block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-blue-500 focus:border-blue-500 sm:text-sm" placeholder="Internal notes for finance/admin (e.g., 'Reconcile to GL Account XYZ')" data-id="element-2506" />
                  </div>}
              </div>
            </div>}
          {currentTab === 'delivery' && <div className="space-y-8" data-id="element-2507">
              {/* Section 1: Output Document Configuration */}
              <div className="border-b border-gray-200 pb-8" data-id="element-2508">
                <h3 className="text-lg font-medium text-gray-900 mb-4" data-id="element-2509">
                  Service Output & Deliverable Configuration
                </h3>
                <div className="bg-gray-50 p-4 rounded-md mb-6" data-id="element-2510">
                  <p className="text-sm text-gray-600" data-id="element-2511">
                    Define the primary official document(s) or outcome(s)
                    produced upon successful completion of this service.
                  </p>
                </div>
                {/* Primary Output Document */}
                <div className="space-y-6" data-id="element-2512">
                  <div data-id="element-2513">
                    <label className="block text-sm font-medium text-gray-700" data-id="element-2514">
                      Primary Output Document Type*
                    </label>
                    <select value={deliveryConfig.primaryOutputDocumentTypeId} onChange={e => setDeliveryConfig({
                  ...deliveryConfig,
                  primaryOutputDocumentTypeId: e.target.value,
                  primaryOutputTemplateId: undefined // Reset template when type changes
                })} className="mt-1 block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:ring-blue-500 focus:border-blue-500 sm:text-sm" data-id="element-2515">
                      <option value="" data-id="element-2516">Select document type...</option>
                      {mockDocumentTypes.map(type => <option key={type.id} value={type.id} data-id="element-2517">
                          {type.name}
                        </option>)}
                    </select>
                    <p className="mt-1 text-sm text-gray-500" data-id="element-2518">
                      Select the main certificate, license, permit, or letter
                      generated.
                    </p>
                  </div>
                  {deliveryConfig.primaryOutputDocumentTypeId && <>
                      <div data-id="element-2519">
                        <label className="block text-sm font-medium text-gray-700" data-id="element-2520">
                          Document Template*
                        </label>
                        <select value={deliveryConfig.primaryOutputTemplateId || ''} onChange={e => setDeliveryConfig({
                    ...deliveryConfig,
                    primaryOutputTemplateId: e.target.value
                  })} className="mt-1 block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:ring-blue-500 focus:border-blue-500 sm:text-sm" data-id="element-2521">
                          <option value="" data-id="element-2522">Select template...</option>
                          {mockDocumentTemplates.filter(t => t.documentTypeId === deliveryConfig.primaryOutputDocumentTypeId).map(template => <option key={template.id} value={template.id} data-id="element-2523">
                                {template.name}
                              </option>)}
                        </select>
                      </div>
                      <div data-id="element-2524">
                        <label className="block text-sm font-medium text-gray-700" data-id="element-2525">
                          Validity Period
                        </label>
                        <div className="mt-1 flex space-x-4" data-id="element-2526">
                          <input type="number" min="1" value={deliveryConfig.outputValidityPeriodValue || ''} onChange={e => setDeliveryConfig({
                      ...deliveryConfig,
                      outputValidityPeriodValue: parseInt(e.target.value)
                    })} className="block w-32 px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:ring-blue-500 focus:border-blue-500 sm:text-sm" placeholder="Duration" data-id="element-2527" />
                          <select value={deliveryConfig.outputValidityPeriodUnit || 'days'} onChange={e => setDeliveryConfig({
                      ...deliveryConfig,
                      outputValidityPeriodUnit: e.target.value as 'days' | 'months' | 'years'
                    })} className="block w-32 px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:ring-blue-500 focus:border-blue-500 sm:text-sm" data-id="element-2528">
                            <option value="days" data-id="element-2529">Days</option>
                            <option value="months" data-id="element-2530">Months</option>
                            <option value="years" data-id="element-2531">Years</option>
                          </select>
                        </div>
                        <p className="mt-1 text-sm text-gray-500" data-id="element-2532">
                          Leave blank if no expiry or fixed expiry date.
                        </p>
                      </div>
                    </>}
                </div>
                {/* Additional Outputs */}
                <div className="mt-8" data-id="element-2533">
                  <div className="flex justify-between items-center mb-4" data-id="element-2534">
                    <h4 className="text-sm font-medium text-gray-900" data-id="element-2535">
                      Additional Outputs
                    </h4>
                    <button onClick={() => {
                  setSelectedOutput(null);
                  setShowOutputModal(true);
                }} className="inline-flex items-center px-3 py-2 border border-transparent text-sm font-medium rounded-md shadow-sm text-white bg-blue-600 hover:bg-blue-700" data-id="element-2536">
                      <PlusIcon className="h-4 w-4 mr-2" data-id="element-2537" />
                      Add Output Document
                    </button>
                  </div>
                  <div className="bg-white shadow ring-1 ring-black ring-opacity-5 rounded-lg overflow-hidden" data-id="element-2538">
                    <table className="min-w-full divide-y divide-gray-300" data-id="element-2539">
                      <thead className="bg-gray-50" data-id="element-2540">
                        <tr data-id="element-2541">
                          <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-2542">
                            Description
                          </th>
                          <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-2543">
                            Document Type
                          </th>
                          <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-2544">
                            Template
                          </th>
                          <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-2545">
                            Validity Period
                          </th>
                          <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-2546">
                            Actions
                          </th>
                        </tr>
                      </thead>
                      <tbody className="bg-white divide-y divide-gray-200" data-id="element-2547">
                        {deliveryConfig.additionalOutputs.length === 0 ? <tr data-id="element-2548">
                            <td colSpan={5} className="px-6 py-8 text-center text-sm text-gray-500" data-id="element-2549">
                              No additional outputs defined yet.
                            </td>
                          </tr> : deliveryConfig.additionalOutputs.map(output => <tr key={output.id} data-id="element-2550">
                              <td className="px-6 py-4 text-sm text-gray-900" data-id="element-2551">
                                {output.description}
                              </td>
                              <td className="px-6 py-4 text-sm text-gray-500" data-id="element-2552">
                                {output.documentTypeName}
                              </td>
                              <td className="px-6 py-4 text-sm text-gray-500" data-id="element-2553">
                                {output.templateName || '-'}
                              </td>
                              <td className="px-6 py-4 text-sm text-gray-500" data-id="element-2554">
                                {output.validityPeriodValue ? `${output.validityPeriodValue} ${output.validityPeriodUnit}` : '-'}
                              </td>
                              <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500" data-id="element-2555">
                                <button onClick={() => {
                          setSelectedOutput(output);
                          setShowOutputModal(true);
                        }} className="text-blue-600 hover:text-blue-900 mr-3" data-id="element-2556">
                                  <PencilIcon className="h-4 w-4" data-id="element-2557" />
                                </button>
                                <button onClick={() => {
                          setDeliveryConfig({
                            ...deliveryConfig,
                            additionalOutputs: deliveryConfig.additionalOutputs.filter(o => o.id !== output.id)
                          });
                        }} className="text-red-600 hover:text-red-900" data-id="element-2558">
                                  <TrashIcon className="h-4 w-4" data-id="element-2559" />
                                </button>
                              </td>
                            </tr>)}
                      </tbody>
                    </table>
                  </div>
                </div>
              </div>
              {/* Section 2: Delivery Methods */}
              <div className="space-y-6" data-id="element-2560">
                <h3 className="text-lg font-medium text-gray-900" data-id="element-2561">
                  Delivery Methods Configuration
                </h3>
                {/* Digital Download */}
                <div className="flex items-center" data-id="element-2562">
                  <input type="checkbox" checked={deliveryConfig.allowPortalDownload} onChange={e => setDeliveryConfig({
                ...deliveryConfig,
                allowPortalDownload: e.target.checked
              })} className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded" data-id="element-2563" />
                  <label className="ml-2 block text-sm text-gray-900" data-id="element-2564">
                    Enable Digital Download via Portal
                  </label>
                </div>
                {/* Office Collection */}
                <div className="space-y-4" data-id="element-2565">
                  <div className="flex items-center" data-id="element-2566">
                    <input type="checkbox" checked={deliveryConfig.allowOfficeCollection} onChange={e => setDeliveryConfig({
                  ...deliveryConfig,
                  allowOfficeCollection: e.target.checked
                })} className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded" data-id="element-2567" />
                    <label className="ml-2 block text-sm text-gray-900" data-id="element-2568">
                      Enable Collection at Office
                    </label>
                  </div>
                  {deliveryConfig.allowOfficeCollection && <>
                      <div data-id="element-2569">
                        <label className="block text-sm font-medium text-gray-700" data-id="element-2570">
                          Default Collection Office(s)*
                        </label>
                        <select multiple value={deliveryConfig.defaultCollectionOffices} onChange={e => {
                    const offices = Array.from(e.target.selectedOptions, option => option.value);
                    setDeliveryConfig({
                      ...deliveryConfig,
                      defaultCollectionOffices: offices
                    });
                  }} className="mt-1 block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:ring-blue-500 focus:border-blue-500 sm:text-sm" data-id="element-2571">
                          {mockCollectionOffices.map(office => <option key={office.id} value={office.id} data-id="element-2572">
                              {office.name}
                            </option>)}
                        </select>
                      </div>
                      <div data-id="element-2573">
                        <label className="block text-sm font-medium text-gray-700" data-id="element-2574">
                          Collection Instructions
                        </label>
                        <textarea value={deliveryConfig.collectionInstructions} onChange={e => setDeliveryConfig({
                    ...deliveryConfig,
                    collectionInstructions: e.target.value
                  })} rows={3} className="mt-1 block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:ring-blue-500 focus:border-blue-500 sm:text-sm" placeholder="e.g., Collect from Woreda 03 Main Office with valid ID..." data-id="element-2575" />
                      </div>
                    </>}
                </div>
                {/* Mail Delivery */}
                <div className="space-y-4" data-id="element-2576">
                  <div className="flex items-center" data-id="element-2577">
                    <input type="checkbox" checked={deliveryConfig.allowMailDelivery} onChange={e => setDeliveryConfig({
                  ...deliveryConfig,
                  allowMailDelivery: e.target.checked
                })} className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded" data-id="element-2578" />
                    <label className="ml-2 block text-sm text-gray-900" data-id="element-2579">
                      Enable Mail Delivery (Physical)
                    </label>
                  </div>
                  {deliveryConfig.allowMailDelivery && <>
                      <div data-id="element-2580">
                        <label className="block text-sm font-medium text-gray-700" data-id="element-2581">
                          Mailing Fee Tariff
                        </label>
                        <select value={deliveryConfig.mailDeliveryFeeId || ''} onChange={e => setDeliveryConfig({
                    ...deliveryConfig,
                    mailDeliveryFeeId: e.target.value
                  })} className="mt-1 block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:ring-blue-500 focus:border-blue-500 sm:text-sm" data-id="element-2582">
                          <option value="" data-id="element-2583">Select a tariff...</option>
                          {tariffs.map(tariff => <option key={tariff.id} value={tariff.id} data-id="element-2584">
                              {tariff.description}
                            </option>)}
                        </select>
                      </div>
                      <div className="flex items-center" data-id="element-2585">
                        <input type="checkbox" checked={deliveryConfig.requiresAddressVerification} onChange={e => setDeliveryConfig({
                    ...deliveryConfig,
                    requiresAddressVerification: e.target.checked
                  })} className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded" data-id="element-2586" />
                        <label className="ml-2 block text-sm text-gray-900" data-id="element-2587">
                          Requires Address Verification Prior to Mailing
                        </label>
                      </div>
                    </>}
                </div>
                {/* Other Delivery Instructions */}
                <div data-id="element-2588">
                  <label className="block text-sm font-medium text-gray-700" data-id="element-2589">
                    Other Delivery Instructions
                  </label>
                  <textarea value={deliveryConfig.otherDeliveryInstructions} onChange={e => setDeliveryConfig({
                ...deliveryConfig,
                otherDeliveryInstructions: e.target.value
              })} rows={3} className="mt-1 block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:ring-blue-500 focus:border-blue-500 sm:text-sm" placeholder="Any additional delivery instructions or notes..." data-id="element-2590" />
                </div>
              </div>
              {/* Section 3: Overall SLA */}
              <div className="space-y-6 pt-8" data-id="element-2591">
                <h3 className="text-lg font-medium text-gray-900" data-id="element-2592">
                  Overall Service Level Agreement (SLA)
                </h3>
                <div data-id="element-2593">
                  <label className="block text-sm font-medium text-gray-700" data-id="element-2594">
                    Overall Target Completion Time (Working Days)*
                  </label>
                  <input type="number" min="1" value={deliveryConfig.slaTargetDays} onChange={e => setDeliveryConfig({
                ...deliveryConfig,
                slaTargetDays: parseInt(e.target.value)
              })} className="mt-1 block w-32 px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:ring-blue-500 focus:border-blue-500 sm:text-sm" data-id="element-2595" />
                  <p className="mt-1 text-sm text-gray-500" data-id="element-2596">
                    Estimated total working days from valid submission to final
                    output/decision.
                  </p>
                </div>
                <div data-id="element-2597">
                  <label className="block text-sm font-medium text-gray-700" data-id="element-2598">
                    SLA Calculation Basis
                  </label>
                  <select value={deliveryConfig.slaBasisEvent} onChange={e => setDeliveryConfig({
                ...deliveryConfig,
                slaBasisEvent: e.target.value as 'submission' | 'payment' | 'last_info'
              })} className="mt-1 block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:ring-blue-500 focus:border-blue-500 sm:text-sm" data-id="element-2599">
                    <option value="submission" data-id="element-2600">
                      From Initial Valid Submission
                    </option>
                    <option value="payment" data-id="element-2601">From Payment Confirmation</option>
                    <option value="last_info" data-id="element-2602">
                      From Last Required Info Received
                    </option>
                  </select>
                </div>
              </div>
            </div>}
          {currentTab === 'communications' && <div className="space-y-8" data-id="element-2603">
              {/* Section 1: Automated Notification Triggers */}
              <div className="border-b border-gray-200 pb-8" data-id="element-2604">
                <h3 className="text-lg font-medium text-gray-900 mb-4" data-id="element-2605">
                  Automated Notification Triggers
                </h3>
                <div className="bg-gray-50 p-4 rounded-md mb-6" data-id="element-2606">
                  <p className="text-sm text-gray-600" data-id="element-2607">
                    Configure automated notifications for key events and status
                    changes. System uses default templates unless overridden.
                    Ensure templates exist.
                  </p>
                </div>
                {/* Notification Rules Table */}
                <div className="mt-4" data-id="element-2608">
                  <div className="flex justify-end mb-4" data-id="element-2609">
                    <button onClick={() => {
                  setSelectedNotification(null);
                  setShowNotificationModal(true);
                }} className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md shadow-sm text-white bg-blue-600 hover:bg-blue-700" data-id="element-2610">
                      <PlusIcon className="h-4 w-4 mr-2" data-id="element-2611" />
                      Add Custom Notification Trigger
                    </button>
                  </div>
                  <div className="bg-white shadow ring-1 ring-black ring-opacity-5 rounded-lg overflow-hidden" data-id="element-2612">
                    <table className="min-w-full divide-y divide-gray-300" data-id="element-2613">
                      <thead className="bg-gray-50" data-id="element-2614">
                        <tr data-id="element-2615">
                          <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-2616">
                            Event/Step
                          </th>
                          <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-2617">
                            Channels
                          </th>
                          <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-2618">
                            Recipients
                          </th>
                          <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-2619">
                            Template
                          </th>
                          <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-2620">
                            Delay
                          </th>
                          <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-2621">
                            Status
                          </th>
                          <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-2622">
                            Actions
                          </th>
                        </tr>
                      </thead>
                      <tbody className="bg-white divide-y divide-gray-200" data-id="element-2623">
                        {notificationRules.map(rule => <tr key={rule.id} data-id="element-2624">
                            <td className="px-6 py-4 text-sm text-gray-900" data-id="element-2625">
                              {rule.triggerEvent}
                            </td>
                            <td className="px-6 py-4 text-sm text-gray-500" data-id="element-2626">
                              <div className="flex space-x-1" data-id="element-2627">
                                {rule.channels.map(channel => <span key={channel} className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-gray-100 text-gray-800" data-id="element-2628">
                                    {channel.toUpperCase()}
                                  </span>)}
                              </div>
                            </td>
                            <td className="px-6 py-4 text-sm text-gray-500" data-id="element-2629">
                              {rule.recipientGroups.join(', ')}
                            </td>
                            <td className="px-6 py-4 text-sm text-gray-500" data-id="element-2630">
                              {rule.templateName || 'Default'}
                            </td>
                            <td className="px-6 py-4 text-sm text-gray-500" data-id="element-2631">
                              {rule.delayValue ? `${rule.delayValue} ${rule.delayUnit} ${rule.delayType}` : '-'}
                            </td>
                            <td className="px-6 py-4 whitespace-nowrap" data-id="element-2632">
                              <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${rule.isEnabled ? 'bg-green-100 text-green-800' : 'bg-gray-100 text-gray-800'}`} data-id="element-2633">
                                {rule.isEnabled ? 'Enabled' : 'Disabled'}
                              </span>
                            </td>
                            <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500" data-id="element-2634">
                              <button onClick={() => {
                          setSelectedNotification(rule);
                          setShowNotificationModal(true);
                        }} className="text-blue-600 hover:text-blue-900 mr-3" data-id="element-2635">
                                <PencilIcon className="h-4 w-4" data-id="element-2636" />
                              </button>
                              <button onClick={() => {
                          setNotificationRules(notificationRules.filter(r => r.id !== rule.id));
                        }} className="text-red-600 hover:text-red-900" data-id="element-2637">
                                <TrashIcon className="h-4 w-4" data-id="element-2638" />
                              </button>
                            </td>
                          </tr>)}
                      </tbody>
                    </table>
                  </div>
                </div>
              </div>
              {/* Section 2: Manual Communication Templates */}
              <div className="space-y-6" data-id="element-2639">
                <h3 className="text-lg font-medium text-gray-900" data-id="element-2640">
                  Ad-hoc Communication Templates
                </h3>
                <div className="bg-gray-50 p-4 rounded-md mb-6" data-id="element-2641">
                  <p className="text-sm text-gray-600" data-id="element-2642">
                    Link pre-defined templates officers can manually send for
                    common issues specific to this service.
                  </p>
                </div>
                <div className="flex justify-end mb-4" data-id="element-2643">
                  <button onClick={() => {
                setSelectedManualTemplate(null);
                setShowTemplateModal(true);
              }} className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md shadow-sm text-white bg-blue-600 hover:bg-blue-700" data-id="element-2644">
                    <PlusIcon className="h-4 w-4 mr-2" data-id="element-2645" />
                    Link Manual Template
                  </button>
                </div>
                <div className="bg-white shadow ring-1 ring-black ring-opacity-5 rounded-lg overflow-hidden" data-id="element-2646">
                  <table className="min-w-full divide-y divide-gray-300" data-id="element-2647">
                    <thead className="bg-gray-50" data-id="element-2648">
                      <tr data-id="element-2649">
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-2650">
                          Template Name
                        </th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-2651">
                          Channel
                        </th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-2652">
                          Purpose
                        </th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-2653">
                          Actions
                        </th>
                      </tr>
                    </thead>
                    <tbody className="bg-white divide-y divide-gray-200" data-id="element-2654">
                      {manualTemplates.length === 0 ? <tr data-id="element-2655">
                          <td colSpan={4} className="px-6 py-8 text-center text-sm text-gray-500" data-id="element-2656">
                            No manual templates linked yet.
                          </td>
                        </tr> : manualTemplates.map(template => <tr key={template.id} data-id="element-2657">
                            <td className="px-6 py-4 text-sm text-gray-900" data-id="element-2658">
                              {template.templateName}
                            </td>
                            <td className="px-6 py-4 text-sm text-gray-500" data-id="element-2659">
                              <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-gray-100 text-gray-800" data-id="element-2660">
                                {template.channel.toUpperCase()}
                              </span>
                            </td>
                            <td className="px-6 py-4 text-sm text-gray-500" data-id="element-2661">
                              {template.purpose}
                            </td>
                            <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500" data-id="element-2662">
                              <button onClick={() => {
                        setManualTemplates(manualTemplates.filter(t => t.id !== template.id));
                      }} className="text-red-600 hover:text-red-900" data-id="element-2663">
                                <TrashIcon className="h-4 w-4" data-id="element-2664" />
                              </button>
                            </td>
                          </tr>)}
                    </tbody>
                  </table>
                </div>
              </div>
            </div>}
          {currentTab === 'review' && <div className="space-y-8" data-id="element-2665">
              {/* Header */}
              <div className="bg-gray-50 p-4 rounded-md" data-id="element-2666">
                <h3 className="text-lg font-medium text-gray-900 mb-2" data-id="element-2667">
                  Review Service Configuration & Activate '
                  {formData.serviceName || 'New Service'}'
                </h3>
                <p className="text-sm text-gray-600" data-id="element-2668">
                  Review all configured details. Click 'Edit Section' to make
                  changes.
                </p>
              </div>
              {/* Collapsible Summary Sections */}
              <div className="space-y-4" data-id="element-2669">
                {/* Core Definition Summary */}
                <div className="border border-gray-200 rounded-lg overflow-hidden" data-id="element-2670">
                  <button onClick={() => setExpandedSection(expandedSection === 'core' ? null : 'core')} className="w-full px-4 py-3 bg-white flex justify-between items-center hover:bg-gray-50" data-id="element-2671">
                    <div className="flex items-center" data-id="element-2672">
                      <h4 className="text-sm font-medium text-gray-900" data-id="element-2673">
                        Core Definition Summary
                      </h4>
                      <button onClick={e => {
                    e.stopPropagation();
                    setCurrentTab('core');
                  }} className="ml-2 text-sm text-blue-600 hover:text-blue-800" data-id="element-2674">
                        Edit Section
                      </button>
                    </div>
                    <ChevronRightIcon className={`h-5 w-5 text-gray-400 transform transition-transform ${expandedSection === 'core' ? 'rotate-90' : ''}`} data-id="element-2675" />
                  </button>
                  {expandedSection === 'core' && <div className="px-4 py-3 bg-gray-50 text-sm text-gray-600" data-id="element-2676">
                      <dl className="grid grid-cols-2 gap-4" data-id="element-2677">
                        <div data-id="element-2678">
                          <dt className="font-medium" data-id="element-2679">Service Code:</dt>
                          <dd data-id="element-2680">{formData.serviceCode}</dd>
                        </div>
                        <div data-id="element-2681">
                          <dt className="font-medium" data-id="element-2682">Service Name:</dt>
                          <dd data-id="element-2683">{formData.serviceName}</dd>
                        </div>
                        <div data-id="element-2684">
                          <dt className="font-medium" data-id="element-2685">Category:</dt>
                          <dd data-id="element-2686">
                            {categories.find(c => c.category_id === formData.categoryId)?.category_name}
                          </dd>
                        </div>
                        <div data-id="element-2687">
                          <dt className="font-medium" data-id="element-2688">Status:</dt>
                          <dd data-id="element-2689">{formData.isActive ? 'Active' : 'Inactive'}</dd>
                        </div>
                      </dl>
                    </div>}
                </div>
                {/* Agency & Scope Summary */}
                <div className="border border-gray-200 rounded-lg overflow-hidden" data-id="element-2690">
                  <button onClick={() => setExpandedSection(expandedSection === 'agency' ? null : 'agency')} className="w-full px-4 py-3 bg-white flex justify-between items-center hover:bg-gray-50" data-id="element-2691">
                    <div className="flex items-center" data-id="element-2692">
                      <h4 className="text-sm font-medium text-gray-900" data-id="element-2693">
                        Agency & Scope Summary
                      </h4>
                      <button onClick={e => {
                    e.stopPropagation();
                    setCurrentTab('agency');
                  }} className="ml-2 text-sm text-blue-600 hover:text-blue-800" data-id="element-2694">
                        Edit Section
                      </button>
                    </div>
                    <ChevronRightIcon className={`h-5 w-5 text-gray-400 transform transition-transform ${expandedSection === 'agency' ? 'rotate-90' : ''}`} data-id="element-2695" />
                  </button>
                  {expandedSection === 'agency' && <div className="px-4 py-3 bg-gray-50 text-sm text-gray-600" data-id="element-2696">
                      <dl className="grid grid-cols-2 gap-4" data-id="element-2697">
                        <div data-id="element-2698">
                          <dt className="font-medium" data-id="element-2699">Primary Agency:</dt>
                          <dd data-id="element-2700">
                            {agencies.find(a => a.agency_id === agencyFormData.responsibleAgencyId)?.agency_name}
                          </dd>
                        </div>
                        <div data-id="element-2701">
                          <dt className="font-medium" data-id="element-2702">Jurisdiction Level:</dt>
                          <dd data-id="element-2703">
                            {jurisdictionLevels.find(l => l.level_id === agencyFormData.jurisdictionLevelId)?.level_name}
                          </dd>
                        </div>
                      </dl>
                    </div>}
                </div>
                {/* Add similar collapsible sections for other tabs... */}
              </div>
              {/* System Validation Summary */}
              <div className="bg-white p-6 border border-gray-200 rounded-lg" data-id="element-2704">
                <h4 className="text-sm font-medium text-gray-900 mb-4" data-id="element-2705">
                  System Validation Summary
                </h4>
                <div className="space-y-3" data-id="element-2706">
                  {validationChecks.map(check => <div key={check.id} className="flex items-center" data-id="element-2707">
                      {check.passed ? <CheckCircleIcon className="h-5 w-5 text-green-500 mr-2" data-id="element-2708" /> : <CircleIcon className="h-5 w-5 text-red-500 mr-2" data-id="element-2709" />}
                      <span className="text-sm text-gray-600" data-id="element-2710">
                        {check.label}
                      </span>
                    </div>)}
                </div>
                <p className="mt-4 text-sm text-gray-500" data-id="element-2711">
                  All critical validations must pass for activation.
                </p>
              </div>
              {/* Activation & Finalization */}
              <div className="bg-white p-6 border border-gray-200 rounded-lg" data-id="element-2712">
                <h4 className="text-sm font-medium text-gray-900 mb-4" data-id="element-2713">
                  Activation & Finalization
                </h4>
                {/* Current Status */}
                <div className="mb-4" data-id="element-2714">
                  <label className="block text-sm font-medium text-gray-700" data-id="element-2715">
                    Current Service Status
                  </label>
                  <div className="mt-1" data-id="element-2716">
                    <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${formData.isActive ? 'bg-green-100 text-green-800' : 'bg-gray-100 text-gray-800'}`} data-id="element-2717">
                      {formData.isActive ? 'Active' : 'Draft'}
                    </span>
                  </div>
                </div>
                {/* Action Selection */}
                <div className="mb-4" data-id="element-2718">
                  <label className="block text-sm font-medium text-gray-700 mb-2" data-id="element-2719">
                    {formData.isActive ? 'Update Service Status:' : 'Set Service Status:'}
                  </label>
                  <div className="space-y-2" data-id="element-2720">
                    {formData.isActive ? <>
                        <label className="flex items-center" data-id="element-2721">
                          <input type="radio" checked={activationAction === 'keep-active'} onChange={() => setActivationAction('keep-active')} className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300" data-id="element-2722" />
                          <span className="ml-2 text-sm text-gray-700" data-id="element-2723">
                            Save Changes & Keep Active
                          </span>
                        </label>
                        <label className="flex items-center" data-id="element-2724">
                          <input type="radio" checked={activationAction === 'deactivate'} onChange={() => setActivationAction('deactivate')} className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300" data-id="element-2725" />
                          <span className="ml-2 text-sm text-gray-700" data-id="element-2726">
                            Deactivate Service
                          </span>
                        </label>
                      </> : <>
                        <label className="flex items-center" data-id="element-2727">
                          <input type="radio" checked={activationAction === 'draft'} onChange={() => setActivationAction('draft')} className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300" data-id="element-2728" />
                          <span className="ml-2 text-sm text-gray-700" data-id="element-2729">
                            Save as Draft (Keep Inactive)
                          </span>
                        </label>
                        <label className="flex items-center" data-id="element-2730">
                          <input type="radio" checked={activationAction === 'activate'} onChange={() => setActivationAction('activate')} className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300" data-id="element-2731" />
                          <span className="ml-2 text-sm text-gray-700" data-id="element-2732">
                            Activate Now (Make Publicly Available)
                          </span>
                        </label>
                      </>}
                  </div>
                </div>
                {/* Administrator Notes */}
                <div className="mb-6" data-id="element-2733">
                  <label className="block text-sm font-medium text-gray-700" data-id="element-2734">
                    Administrator Notes
                    {(activationAction === 'activate' || activationAction === 'deactivate') && <span className="text-red-500" data-id="element-2735">*</span>}
                  </label>
                  <textarea value={adminNotes} onChange={e => setAdminNotes(e.target.value)} rows={3} className="mt-1 block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-blue-500 focus:border-blue-500 sm:text-sm" placeholder="Reason for activation, deactivation, or major changes..." data-id="element-2736" />
                  <p className="mt-1 text-sm text-gray-500" data-id="element-2737">
                    Logged for audit purposes.
                  </p>
                </div>
                {/* Action Buttons */}
                <div className="flex justify-between" data-id="element-2738">
                  <div className="space-x-3" data-id="element-2739">
                    <button onClick={() => setCurrentTab('communications')} className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50" data-id="element-2740">
                      Previous: Communications
                    </button>
                    <button onClick={() => {}} className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50" data-id="element-2741">
                      Cancel All Changes
                    </button>
                  </div>
                  <div className="space-x-3" data-id="element-2742">
                    {activationAction === 'draft' && <button onClick={() => {}} className="px-4 py-2 text-sm font-medium text-white bg-blue-600 rounded-md hover:bg-blue-700" data-id="element-2743">
                        Save Service as Draft
                      </button>}
                    {activationAction === 'activate' && <button onClick={() => {}} disabled={!isValidForActivation} className="px-4 py-2 text-sm font-medium text-white bg-blue-600 rounded-md hover:bg-blue-700 disabled:bg-blue-300 disabled:cursor-not-allowed" data-id="element-2744">
                        Save & Activate Service
                      </button>}
                    {activationAction === 'keep-active' && <button onClick={() => {}} className="px-4 py-2 text-sm font-medium text-white bg-blue-600 rounded-md hover:bg-blue-700" data-id="element-2745">
                        Save Changes & Keep Active
                      </button>}
                    {activationAction === 'deactivate' && <button onClick={() => {}} className="px-4 py-2 text-sm font-medium text-white bg-red-600 rounded-md hover:bg-red-700" data-id="element-2746">
                        Save Changes & Deactivate Service
                      </button>}
                  </div>
                </div>
              </div>
            </div>}
        </div>
        {/* Action Buttons */}
        <div className="mt-6 flex justify-between" data-id="element-2747">
          <button className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50" disabled={currentTab === 'core'} onClick={() => {
          const tabOrder: TabId[] = ['core', 'agency', 'workflow', 'documents', 'fees', 'delivery', 'communications', 'review'];
          const currentIndex = tabOrder.indexOf(currentTab);
          setCurrentTab(tabOrder[currentIndex - 1]);
        }} data-id="element-2748">
            Previous
          </button>
          <button className="px-4 py-2 text-sm font-medium text-white bg-blue-600 rounded-md hover:bg-blue-700" onClick={() => {
          if (currentTab === 'core') {
            if (validateForm()) {
              setIsDraft(true); // Enable access to other tabs
              setCurrentTab('agency');
            }
          } else if (currentTab === 'review') {
            // Handle final submission
            console.log('Submitting form...');
          } else {
            // For other tabs, just proceed to the next one
            const nextTab = getNextTab(currentTab);
            if (nextTab) {
              setCurrentTab(nextTab);
            }
          }
        }} data-id="element-2749">
            {currentTab === 'review' ? 'Review & Submit' : 'Save & Continue'}
          </button>
        </div>
      </div>
      {/* Override Modal */}
      {showOverrideModal && <div className="fixed inset-0 bg-gray-500 bg-opacity-75 flex items-center justify-center" data-id="element-2750">
          <div className="bg-white rounded-lg p-6 max-w-lg w-full" data-id="element-2751">
            <h3 className="text-lg font-medium text-gray-900 mb-4" data-id="element-2752">
              {currentOverride ? 'Edit Override' : 'Add New Override'}
            </h3>
            {/* Modal content - form fields for override */}
            <div className="space-y-4" data-id="element-2753">
              <div data-id="element-2754">
                <label className="block text-sm font-medium text-gray-700" data-id="element-2755">
                  Jurisdiction Level*
                </label>
                <select className="mt-1 block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:ring-blue-500 focus:border-blue-500 sm:text-sm" value={currentOverride?.level || ''} onChange={e => setCurrentOverride(prev => ({
              ...(prev || {
                id: Math.random().toString(36).substring(2, 9),
                jurisdiction: '',
                isAvailable: true,
                cascades: true,
                notes: ''
              }),
              level: e.target.value
            }))} data-id="element-2756">
                  <option value="" data-id="element-2757">Select a level</option>
                  {jurisdictionLevels.map(level => <option key={level.level_id} value={level.level_name} data-id="element-2758">
                      {level.level_name}
                    </option>)}
                </select>
              </div>
              <div data-id="element-2759">
                <label className="block text-sm font-medium text-gray-700" data-id="element-2760">
                  Jurisdiction Path*
                </label>
                <input type="text" className="mt-1 block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:ring-blue-500 focus:border-blue-500 sm:text-sm" placeholder="e.g., Addis Ababa/Kirkos" value={currentOverride?.jurisdiction || ''} onChange={e => setCurrentOverride(prev => ({
              ...(prev || {
                id: Math.random().toString(36).substring(2, 9),
                level: '',
                isAvailable: true,
                cascades: true,
                notes: ''
              }),
              jurisdiction: e.target.value
            }))} data-id="element-2761" />
                <p className="mt-1 text-xs text-gray-500" data-id="element-2762">
                  Enter the specific jurisdiction path where this override
                  applies
                </p>
              </div>
              <div className="flex items-center" data-id="element-2763">
                <input type="checkbox" id="isAvailable" className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded" checked={currentOverride?.isAvailable || false} onChange={e => setCurrentOverride(prev => ({
              ...(prev || {
                id: Math.random().toString(36).substring(2, 9),
                level: '',
                jurisdiction: '',
                cascades: true,
                notes: ''
              }),
              isAvailable: e.target.checked
            }))} data-id="element-2764" />
                <label htmlFor="isAvailable" className="ml-2 block text-sm text-gray-900" data-id="element-2765">
                  Service is available in this jurisdiction
                </label>
              </div>
              <div className="flex items-center" data-id="element-2766">
                <input type="checkbox" id="cascades" className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded" checked={currentOverride?.cascades || false} onChange={e => setCurrentOverride(prev => ({
              ...(prev || {
                id: Math.random().toString(36).substring(2, 9),
                level: '',
                jurisdiction: '',
                isAvailable: true,
                notes: ''
              }),
              cascades: e.target.checked
            }))} data-id="element-2767" />
                <label htmlFor="cascades" className="ml-2 block text-sm text-gray-900" data-id="element-2768">
                  Override cascades to sub-jurisdictions
                </label>
              </div>
              <div data-id="element-2769">
                <label className="block text-sm font-medium text-gray-700" data-id="element-2770">
                  Notes
                </label>
                <textarea rows={3} className="mt-1 block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:ring-blue-500 focus:border-blue-500 sm:text-sm" placeholder="Any additional notes about this override" value={currentOverride?.notes || ''} onChange={e => setCurrentOverride(prev => ({
              ...(prev || {
                id: Math.random().toString(36).substring(2, 9),
                level: '',
                jurisdiction: '',
                isAvailable: true,
                cascades: true
              }),
              notes: e.target.value
            }))} data-id="element-2771" />
              </div>
              <div className="mt-6 flex justify-end space-x-3" data-id="element-2772">
                <button onClick={() => {
              setCurrentOverride(null);
              setShowOverrideModal(false);
            }} className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50" data-id="element-2773">
                  Cancel
                </button>
                <button onClick={() => {
              if (currentOverride && currentOverride.level && currentOverride.jurisdiction) {
                // For edit mode, replace the existing override
                if (agencyFormData.overrides.some(o => o.id === currentOverride.id)) {
                  setAgencyFormData({
                    ...agencyFormData,
                    overrides: agencyFormData.overrides.map(o => o.id === currentOverride.id ? currentOverride : o)
                  });
                } else {
                  // For add mode, append the new override
                  setAgencyFormData({
                    ...agencyFormData,
                    overrides: [...agencyFormData.overrides, currentOverride]
                  });
                }
                setCurrentOverride(null);
                setShowOverrideModal(false);
              }
            }} disabled={!currentOverride?.level || !currentOverride?.jurisdiction} className="px-4 py-2 text-sm font-medium text-white bg-blue-600 rounded-md hover:bg-blue-700 disabled:bg-blue-300 disabled:cursor-not-allowed" data-id="element-2774">
                  Save Override
                </button>
              </div>
            </div>
          </div>
        </div>}
      {/* Step Configuration Modal */}
      {showStepModal && selectedStep && <div className="fixed inset-0 bg-gray-500 bg-opacity-75 flex items-center justify-center" data-id="element-2775">
          <div className="bg-white rounded-lg p-6 max-w-lg w-full" data-id="element-2776">
            <h3 className="text-lg font-medium text-gray-900 mb-4" data-id="element-2777">
              Configure Step: {selectedStep.step_name}
            </h3>
            <div className="space-y-4" data-id="element-2778">
              <div data-id="element-2779">
                <label className="block text-sm font-medium text-gray-700" data-id="element-2780">
                  Default Assigned Role
                </label>
                <input type="text" value={selectedStep.default_role} disabled className="mt-1 block w-full px-3 py-2 bg-gray-100 border border-gray-300 rounded-md shadow-sm text-gray-500 sm:text-sm" data-id="element-2781" />
              </div>
              <div data-id="element-2782">
                <label className="block text-sm font-medium text-gray-700" data-id="element-2783">
                  Service-Specific Role Override
                </label>
                <select value={selectedStep.overridden_role || ''} onChange={e => {
              const newSteps = workflowSteps.map(step => step.step_id === selectedStep.step_id ? {
                ...step,
                overridden_role: e.target.value || undefined
              } : step);
              setWorkflowSteps(newSteps);
            }} className="mt-1 block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm sm:text-sm" data-id="element-2784">
                  <option value="" data-id="element-2785">Use default role</option>
                  <option value="Senior Officer" data-id="element-2786">Senior Officer</option>
                  <option value="Specialist" data-id="element-2787">Specialist</option>
                  <option value="Manager" data-id="element-2788">Manager</option>
                </select>
              </div>
              <div data-id="element-2789">
                <label className="block text-sm font-medium text-gray-700" data-id="element-2790">
                  Default SLA (Working Days)
                </label>
                <input type="number" value={selectedStep.default_sla_days} disabled className="mt-1 block w-full px-3 py-2 bg-gray-100 border border-gray-300 rounded-md shadow-sm text-gray-500 sm:text-sm" data-id="element-2791" />
              </div>
              <div data-id="element-2792">
                <label className="block text-sm font-medium text-gray-700" data-id="element-2793">
                  Service-Specific SLA Override (Working Days)
                </label>
                <input type="number" value={selectedStep.overridden_sla_days || ''} onChange={e => {
              const newSteps = workflowSteps.map(step => step.step_id === selectedStep.step_id ? {
                ...step,
                overridden_sla_days: e.target.value ? parseInt(e.target.value) : undefined
              } : step);
              setWorkflowSteps(newSteps);
            }} placeholder="Enter days" className="mt-1 block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm sm:text-sm" data-id="element-2794" />
              </div>
              <div className="mt-6 flex justify-end space-x-3" data-id="element-2795">
                <button onClick={() => setShowStepModal(false)} className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50" data-id="element-2796">
                  Cancel
                </button>
                <button onClick={() => setShowStepModal(false)} className="px-4 py-2 text-sm font-medium text-white bg-blue-600 rounded-md hover:bg-blue-700" data-id="element-2797">
                  Save Changes
                </button>
              </div>
            </div>
          </div>
        </div>}
      {/* Document Requirement Modal */}
      {showDocumentModal && <div className="fixed inset-0 bg-gray-500 bg-opacity-75 flex items-center justify-center" data-id="element-2798">
          <div className="bg-white rounded-lg p-6 max-w-2xl w-full" data-id="element-2799">
            <h3 className="text-lg font-medium text-gray-900 mb-4" data-id="element-2800">
              {selectedDocument ? 'Edit Document Requirement' : 'Add New Document Requirement'}
            </h3>
            <div className="space-y-4" data-id="element-2801">
              {/* Document Type */}
              <div data-id="element-2802">
                <label className="block text-sm font-medium text-gray-700" data-id="element-2803">
                  Document Type*
                </label>
                <div className="mt-1 flex items-center" data-id="element-2804">
                  <select className="block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:ring-blue-500 focus:border-blue-500 sm:text-sm" value={selectedDocument?.documentTypeId || ''} onChange={e => {
                const docType = mockDocumentTypes.find(dt => dt.id === e.target.value);
                if (docType) {
                  setSelectedDocument(prev => ({
                    ...(prev || {
                      id: Math.random().toString(),
                      isMandatory: true,
                      conditionLogic: '',
                      targetJurisdiction: '',
                      targetApplicantTypes: [],
                      helpText: '',
                      isActive: true
                    }),
                    documentTypeId: docType.id,
                    documentTypeName: docType.name
                  }));
                }
              }} data-id="element-2805">
                    <option value="" data-id="element-2806">Select a document type...</option>
                    {mockDocumentTypes.map(dt => <option key={dt.id} value={dt.id} data-id="element-2807">
                        {dt.name}
                      </option>)}
                  </select>
                  <button className="ml-2 text-sm text-blue-600 hover:text-blue-500" data-id="element-2808">
                    + New Type
                  </button>
                </div>
              </div>
              {/* Is Mandatory */}
              <div className="flex items-center" data-id="element-2809">
                <input type="checkbox" checked={selectedDocument?.isMandatory || false} onChange={e => {
              setSelectedDocument(prev => prev ? {
                ...prev,
                isMandatory: e.target.checked
              } : null);
            }} className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded" data-id="element-2810" />
                <label className="ml-2 block text-sm text-gray-900" data-id="element-2811">
                  Mandatory for this service
                </label>
              </div>
              {/* Condition Logic */}
              <div data-id="element-2812">
                <label className="block text-sm font-medium text-gray-700" data-id="element-2813">
                  Requirement Condition Logic
                </label>
                <textarea value={selectedDocument?.conditionLogic || ''} onChange={e => {
              setSelectedDocument(prev => prev ? {
                ...prev,
                conditionLogic: e.target.value
              } : null);
            }} rows={3} className="mt-1 block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:ring-blue-500 focus:border-blue-500 sm:text-sm" placeholder="e.g., Required if applicant is corporate" data-id="element-2814" />
              </div>
              {/* Target Jurisdiction */}
              <div data-id="element-2815">
                <label className="block text-sm font-medium text-gray-700" data-id="element-2816">
                  Target Jurisdiction
                </label>
                <select value={selectedDocument?.targetJurisdiction || ''} onChange={e => {
              setSelectedDocument(prev => prev ? {
                ...prev,
                targetJurisdiction: e.target.value
              } : null);
            }} className="mt-1 block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:ring-blue-500 focus:border-blue-500 sm:text-sm" data-id="element-2817">
                  <option value="" data-id="element-2818">All Jurisdictions</option>
                  {jurisdictionLevels.map(level => <option key={level.level_id} value={level.level_name} data-id="element-2819">
                      {level.level_name}
                    </option>)}
                </select>
              </div>
              {/* Target Applicant Types */}
              <div data-id="element-2820">
                <label className="block text-sm font-medium text-gray-700" data-id="element-2821">
                  Target Applicant Types
                </label>
                <div className="mt-2 space-y-2" data-id="element-2822">
                  {audienceTypes.map(type => <label key={type} className="inline-flex items-center mr-4" data-id="element-2823">
                      <input type="checkbox" checked={selectedDocument?.targetApplicantTypes.includes(type) || false} onChange={e => {
                  setSelectedDocument(prev => {
                    if (!prev) return null;
                    const types = e.target.checked ? [...prev.targetApplicantTypes, type] : prev.targetApplicantTypes.filter(t => t !== type);
                    return {
                      ...prev,
                      targetApplicantTypes: types
                    };
                  });
                }} className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded" data-id="element-2824" />
                      <span className="ml-2 text-sm text-gray-700" data-id="element-2825">{type}</span>
                    </label>)}
                </div>
              </div>
              {/* Help Text */}
              <div data-id="element-2826">
                <label className="block text-sm font-medium text-gray-700" data-id="element-2827">
                  Help Text for Applicant
                </label>
                <textarea value={selectedDocument?.helpText || ''} onChange={e => {
              setSelectedDocument(prev => prev ? {
                ...prev,
                helpText: e.target.value
              } : null);
            }} rows={2} className="mt-1 block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:ring-blue-500 focus:border-blue-500 sm:text-sm" placeholder="e.g., Must be notarized, Not older than 6 months" data-id="element-2828" />
              </div>
              {/* Is Active */}
              <div className="flex items-center" data-id="element-2829">
                <input type="checkbox" checked={selectedDocument?.isActive || false} onChange={e => {
              setSelectedDocument(prev => prev ? {
                ...prev,
                isActive: e.target.checked
              } : null);
            }} className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded" data-id="element-2830" />
                <label className="ml-2 block text-sm text-gray-900" data-id="element-2831">
                  Requirement is active
                </label>
              </div>
              {/* Modal Actions */}
              <div className="mt-6 flex justify-end space-x-3" data-id="element-2832">
                <button onClick={() => setShowDocumentModal(false)} className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50" data-id="element-2833">
                  Cancel
                </button>
                <button onClick={() => {
              if (selectedDocument) {
                if (selectedDocument.id) {
                  setDocumentRequirements(prev => prev.map(d => d.id === selectedDocument.id ? selectedDocument : d));
                } else {
                  setDocumentRequirements(prev => [...prev, selectedDocument]);
                }
                setShowDocumentModal(false);
              }
            }} className="px-4 py-2 text-sm font-medium text-white bg-blue-600 rounded-md hover:bg-blue-700" data-id="element-2834">
                  {selectedDocument?.id ? 'Save Changes' : 'Add Requirement'}
                </button>
              </div>
            </div>
          </div>
        </div>}
      {/* Tariff Modal */}
      {showTariffModal && <div className="fixed inset-0 bg-gray-500 bg-opacity-75 flex items-center justify-center" data-id="element-2835">
          <div className="bg-white rounded-lg p-6 max-w-2xl w-full" data-id="element-2836">
            <h3 className="text-lg font-medium text-gray-900 mb-4" data-id="element-2837">
              {selectedTariff ? 'Edit Tariff' : 'Add New Tariff'}
            </h3>
            <div className="space-y-4" data-id="element-2838">
              {/* Description */}
              <div data-id="element-2839">
                <label className="block text-sm font-medium text-gray-700" data-id="element-2840">
                  Tariff Description*
                </label>
                <input type="text" value={selectedTariff?.description || ''} onChange={e => setSelectedTariff(prev => prev ? {
              ...prev,
              description: e.target.value
            } : null)} className="mt-1 block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-blue-500 focus:border-blue-500 sm:text-sm" placeholder="e.g., Standard Application Fee - Individual" data-id="element-2841" />
              </div>
              {/* Revenue Type */}
              <div data-id="element-2842">
                <label className="block text-sm font-medium text-gray-700" data-id="element-2843">
                  Revenue Type*
                </label>
                <select value={selectedTariff?.revenueType || ''} onChange={e => setSelectedTariff(prev => prev ? {
              ...prev,
              revenueType: e.target.value
            } : null)} className="mt-1 block w-full pl-3 pr-10 py-2 text-base border-gray-300 focus:outline-none focus:ring-blue-500 focus:border-blue-500 sm:text-sm rounded-md" data-id="element-2844">
                  <option value="" data-id="element-2845">Select a revenue type...</option>
                  {mockRevenueTypes.map(type => <option key={type.id} value={type.id} data-id="element-2846">
                      {type.name}
                    </option>)}
                </select>
              </div>
              {/* Amount and Currency */}
              <div className="grid grid-cols-2 gap-4" data-id="element-2847">
                <div data-id="element-2848">
                  <label className="block text-sm font-medium text-gray-700" data-id="element-2849">
                    Amount/Rate*
                  </label>
                  <input type="number" value={selectedTariff?.amount || ''} onChange={e => setSelectedTariff(prev => prev ? {
                ...prev,
                amount: parseFloat(e.target.value)
              } : null)} className="mt-1 block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-blue-500 focus:border-blue-500 sm:text-sm" data-id="element-2850" />
                </div>
                <div data-id="element-2851">
                  <label className="block text-sm font-medium text-gray-700" data-id="element-2852">
                    Currency*
                  </label>
                  <select value={selectedTariff?.currency || 'ETB'} onChange={e => setSelectedTariff(prev => prev ? {
                ...prev,
                currency: e.target.value
              } : null)} className="mt-1 block w-full pl-3 pr-10 py-2 text-base border-gray-300 focus:outline-none focus:ring-blue-500 focus:border-blue-500 sm:text-sm rounded-md" data-id="element-2853">
                    <option value="ETB" data-id="element-2854">ETB</option>
                    <option value="USD" data-id="element-2855">USD</option>
                  </select>
                </div>
              </div>
              {/* Effective Dates */}
              <div className="grid grid-cols-2 gap-4" data-id="element-2856">
                <div data-id="element-2857">
                  <label className="block text-sm font-medium text-gray-700" data-id="element-2858">
                    Effective From*
                  </label>
                  <input type="date" value={selectedTariff?.effectiveFrom || ''} onChange={e => setSelectedTariff(prev => prev ? {
                ...prev,
                effectiveFrom: e.target.value
              } : null)} className="mt-1 block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-blue-500 focus:border-blue-500 sm:text-sm" data-id="element-2859" />
                </div>
                <div data-id="element-2860">
                  <label className="block text-sm font-medium text-gray-700" data-id="element-2861">
                    Effective Until
                  </label>
                  <input type="date" value={selectedTariff?.effectiveUntil || ''} onChange={e => setSelectedTariff(prev => prev ? {
                ...prev,
                effectiveUntil: e.target.value
              } : null)} className="mt-1 block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-blue-500 focus:border-blue-500 sm:text-sm" data-id="element-2862" />
                </div>
              </div>
              {/* Active Status */}
              <div className="flex items-center" data-id="element-2863">
                <input type="checkbox" checked={selectedTariff?.isActive || false} onChange={e => setSelectedTariff(prev => prev ? {
              ...prev,
              isActive: e.target.checked
            } : null)} className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded" data-id="element-2864" />
                <label className="ml-2 block text-sm text-gray-900" data-id="element-2865">
                  Tariff is active
                </label>
              </div>
              {/* Modal Actions */}
              <div className="mt-6 flex justify-end space-x-3" data-id="element-2866">
                <button onClick={() => setShowTariffModal(false)} className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50" data-id="element-2867">
                  Cancel
                </button>
                <button onClick={() => {
              if (selectedTariff) {
                const newTariff = {
                  ...selectedTariff,
                  id: selectedTariff.id || Math.random().toString()
                };
                setTariffs(prev => selectedTariff.id ? prev.map(t => t.id === selectedTariff.id ? newTariff : t) : [...prev, newTariff]);
                setShowTariffModal(false);
              }
            }} className="px-4 py-2 text-sm font-medium text-white bg-blue-600 rounded-md hover:bg-blue-700" data-id="element-2868">
                  {selectedTariff?.id ? 'Save Changes' : 'Add Tariff'}
                </button>
              </div>
            </div>
          </div>
        </div>}
      {/* Manual Template Modal */}
      {showTemplateModal && <div className="fixed inset-0 bg-gray-500 bg-opacity-75 flex items-center justify-center" data-id="element-2869">
          <div className="bg-white rounded-lg p-6 max-w-2xl w-full" data-id="element-2870">
            <h3 className="text-lg font-medium text-gray-900 mb-4" data-id="element-2871">
              Link Manual Communication Template
            </h3>
            <div className="space-y-4" data-id="element-2872">
              {/* Template Selection */}
              <div data-id="element-2873">
                <label className="block text-sm font-medium text-gray-700" data-id="element-2874">
                  Select Template*
                </label>
                <select className="mt-1 block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:ring-blue-500 focus:border-blue-500 sm:text-sm" onChange={e => {
              const template = mockMessageTemplates.find(t => t.id === e.target.value);
              if (template) {
                const newTemplate: ManualTemplate = {
                  id: Math.random().toString(),
                  templateId: template.id,
                  templateName: template.name,
                  channel: template.channel as 'email' | 'sms' | 'portal',
                  purpose: ''
                };
                setSelectedManualTemplate(newTemplate);
              }
            }} data-id="element-2875">
                  <option value="" data-id="element-2876">Choose a template...</option>
                  {mockMessageTemplates.map(template => <option key={template.id} value={template.id} data-id="element-2877">
                      {template.name}
                    </option>)}
                </select>
              </div>
              {/* Purpose/Usage Notes */}
              <div data-id="element-2878">
                <label className="block text-sm font-medium text-gray-700" data-id="element-2879">
                  Purpose/Usage Notes*
                </label>
                <textarea rows={3} className="mt-1 block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:ring-blue-500 focus:border-blue-500 sm:text-sm" placeholder="Describe when officers should use this template..." value={selectedManualTemplate?.purpose || ''} onChange={e => {
              if (selectedManualTemplate) {
                setSelectedManualTemplate({
                  ...selectedManualTemplate,
                  purpose: e.target.value
                });
              }
            }} data-id="element-2880" />
              </div>
              {/* Preview Button */}
              <div data-id="element-2881">
                <button onClick={() => {
              // Handle template preview
              console.log('Preview template:', selectedManualTemplate);
            }} className="inline-flex items-center px-3 py-2 border border-gray-300 rounded-md text-sm font-medium text-gray-700 bg-white hover:bg-gray-50" data-id="element-2882">
                  Preview Template
                </button>
              </div>
              {/* Modal Actions */}
              <div className="mt-6 flex justify-end space-x-3" data-id="element-2883">
                <button onClick={() => {
              setShowTemplateModal(false);
              setSelectedManualTemplate(null);
            }} className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50" data-id="element-2884">
                  Cancel
                </button>
                <button onClick={() => {
              if (selectedManualTemplate?.templateId && selectedManualTemplate.purpose) {
                setManualTemplates(prev => [...prev, selectedManualTemplate]);
                setShowTemplateModal(false);
                setSelectedManualTemplate(null);
              }
            }} disabled={!selectedManualTemplate?.templateId || !selectedManualTemplate.purpose} className="px-4 py-2 text-sm font-medium text-white bg-blue-600 rounded-md hover:bg-blue-700 disabled:bg-blue-300 disabled:cursor-not-allowed" data-id="element-2885">
                  Link Template
                </button>
              </div>
            </div>
          </div>
        </div>}
    </div>;
};